/**
 * Reports Page
 * Financial reports and analytics with data preview
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { apiGet, apiDownload, extractApiData, describeApiError, REPORT_TIMEOUT } from '../utils/apiClient';
import {
  exportContractRegister,
  exportPaymentSchedule,
  exportInvestorSummary,
  exportAuditTrail,
  exportCurrentLongTerm,
  exportRolledContracts,
  exportRolloverFundFlow,
  exportContractSuspense,
} from '../utils/excelExport';
import { format } from 'date-fns';
import { formatNumber as formatNum } from '../utils/formatters';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useNotification } from '../contexts/NotificationContext';
import { MODULES } from '../contexts/PermissionConstants';
import ReservePayoutDialog from '../components/contracts/ReservePayoutDialog';
import logger from '../utils/logger';

// Contract Register row cap. Deliberately NOT raised to the 100000 used by Investor
// Summary / Audit Trail: each register row now carries a per-investor jsonb payload,
// so a large unfiltered pull is materially heavier than those tabs. We request
// CONTRACT_REGISTER_LIMIT + 1 and, if the extra row arrives, tell the user the results
// were truncated rather than showing a short list that looks complete.
const CONTRACT_REGISTER_LIMIT = 500;

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd MMM yyyy');
  } catch {
    return dateStr;
  }
};

// T3-065: Related/External classification chip for the journal + issuer-ledger previews.
// party_class comes from the report function (resolved per line: frozen-at-export value if
// the line was exported, else the live investor flag). It signals which GL pair the line
// posted to — Related uses the product's related_* pair (or falls back to the base pair).
const renderPartyClassChip = (partyClass) => {
  if (!partyClass) return '-';
  const isRelated = partyClass === 'Related';
  return (
    <Chip
      label={partyClass}
      size="small"
      variant="outlined"
      color={isRelated ? 'warning' : 'default'}
    />
  );
};

// Build a concise, human-readable summary of an audit entry's change for the
// inline "Changes" cell. Surfaces the right side per action:
//   INSERT — the created record's identifying fields (from new_values)
//   DELETE — the deleted record's identifying fields (from old_values) so the
//            details of what was removed are visible (new_values is NULL on a delete)
//   UPDATE — only the fields that actually changed, as "field: old → new"
// Internal/noise columns (timestamps, audit bookkeeping) are skipped.
const AUDIT_SUMMARY_SKIP_KEYS = new Set([
  'created_at', 'updated_at', 'created_by', 'updated_by',
  // 'action' is an internal audit tag (e.g. 'create_historical', 'historical_investor_amend'), not a
  // changed field — keep it out of the Changes summary. 'reason' is handled specially in
  // summarizeAuditChange (rendered as a trailing "(reason: …)" note, not a ∅→value diff).
  'action',
]);

const formatAuditValue = (val) => {
  if (val === null || val === undefined || val === '') return '∅';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

// Prefer the most identifying fields first so the truncated summary is useful.
const AUDIT_PREFERRED_KEYS = ['reference', 'user_reference', 'name', 'status', 'capital_amount', 'investor_id'];

const summarizeAuditRecord = (obj) => {
  if (!obj || typeof obj !== 'object') return '-';
  const keys = Object.keys(obj).filter((k) => !AUDIT_SUMMARY_SKIP_KEYS.has(k));
  const ordered = [
    ...AUDIT_PREFERRED_KEYS.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !AUDIT_PREFERRED_KEYS.includes(k)),
  ];
  const parts = ordered.map((k) => `${k}: ${formatAuditValue(obj[k])}`);
  return parts.length ? parts.join(', ') : '-';
};

// Friendly, singular labels for the tables a merge re-points (keys come from the merge
// SP's `repointed` payload). Key-agnostic fallback humanises any table not listed here,
// so the summary stays correct if the SP adds a table later.
const MERGE_REPOINT_LABELS = {
  contract_master: 'contract',
  capital_receipts: 'capital receipt',
  client_documents: 'document',
  contract_amortization_schedule: 'schedule row',
  contract_finalisation: 'finalisation',
  refdata_commission_rates: 'commission rate',
  user_approval_audit: 'approval record',
  contract_investors: 'contract link',
  stakeholder_investor_issuer: 'issuer link',
};

const humaniseKey = (k) => k.replace(/_/g, ' ');

// "1 contract, 2 documents" from the repointed count object; only non-zero tables.
const summarizeMergeRepointed = (repointed) => {
  if (!repointed || typeof repointed !== 'object') return 'no linked records';
  const parts = Object.entries(repointed)
    .filter(([, n]) => Number(n) > 0)
    .map(([k, n]) => {
      const label = MERGE_REPOINT_LABELS[k] || humaniseKey(k);
      const count = Number(n);
      return `${count} ${label}${count === 1 ? '' : 's'}`;
    });
  return parts.length ? parts.join(', ') : 'no linked records';
};

// A merge stores structurally-disjoint old/new objects (old = {survivor, loser} snapshots,
// new = {survivor_id, loser_id, loser_deactivated, repointed}). The generic field-diff can
// find no shared keys, so format the known payload into a readable action summary instead.
const summarizeAuditMerge = (oldV, newV) => {
  const surv = oldV?.survivor || {};
  const lose = oldV?.loser || {};
  // Investor merges carry `name`; contract merges carry `reference`. Show whichever exists,
  // then the id in parens; when neither label exists, just show the id once.
  const survId = newV?.survivor_id ?? surv.id;
  const loseId = newV?.loser_id ?? lose.id;
  const label = (obj, id) => {
    const name = obj.name || obj.reference;
    return name ? `"${name}" (#${id})` : `#${id}`;
  };
  const loserInactive = lose.active === false ? ' (inactive)' : '';
  const moved = summarizeMergeRepointed(newV?.repointed);
  const deactivated = newV?.loser_deactivated ? '; loser deactivated' : '';
  return `Merged ${label(lose, loseId)}${loserInactive} into ${label(surv, survId)}; moved ${moved}${deactivated}`;
};

const summarizeAuditChange = (audit) => {
  if (!audit) return '-';
  const { action, old_values: oldV, new_values: newV } = audit;

  if (action === 'DELETE') {
    return summarizeAuditRecord(oldV);
  }
  if (action === 'INSERT') {
    return summarizeAuditRecord(newV);
  }
  // MERGE (T3-075): contract / investor merges record { loser_id / loser, survivor_id / survivor }.
  // Render names + the per-table repointed counts, falling back to a bare id summary when the
  // payload lacks the survivor/loser id (e.g. an older or differently-shaped merge entry).
  if (action === 'MERGE') {
    const surv = newV?.survivor_id ?? oldV?.survivor?.id ?? oldV?.survivor?.reference;
    const lose = newV?.loser_id ?? oldV?.loser?.id ?? oldV?.loser?.reference;
    if (surv != null && lose != null) return summarizeAuditMerge(oldV, newV);
    return summarizeAuditRecord(newV || oldV);
  }
  // UPDATE (and anything else with both sides): show only changed fields.
  if (oldV && newV) {
    const changed = [];
    const keys = new Set([...Object.keys(oldV), ...Object.keys(newV)]);
    keys.forEach((k) => {
      if (AUDIT_SUMMARY_SKIP_KEYS.has(k)) return;
      // 'reason' is a free-text note that accompanies an amend, not a changed field. The amend SPs
      // carry it on BOTH sides (old=null, new=text) only so it survives the both-sides gate below —
      // but rendering it as a diff produces the misleading "reason: ∅ → history". Skip it here and
      // append it as a trailing "(reason: …)" note after the real field diffs.
      if (k === 'reason') return;
      // A key ABSENT from one side isn't part of this change — skip it. Legacy audit rows
      // (before T3-063) wrote a PARTIAL new_values keyed off submitted parameters, so most
      // columns were simply missing from new_values; diffing them against a full old_values
      // made every such column render as "value → ∅" even though nothing was cleared. Only a
      // key present on BOTH sides with genuinely different values is a real change (a real
      // clear-to-null is still present-but-null on the new side, so it still shows).
      if (!Object.prototype.hasOwnProperty.call(oldV, k)
        || !Object.prototype.hasOwnProperty.call(newV, k)) return;
      const before = oldV[k];
      const after = newV[k];
      if (JSON.stringify(before) !== JSON.stringify(after)) {
        changed.push(`${k}: ${formatAuditValue(before)} → ${formatAuditValue(after)}`);
      }
    });
    // Trailing reason note (from the new side), when present — e.g.
    // "investor_capital: 100000 → 50000 (reason: history)".
    const reasonNote = (newV.reason != null && String(newV.reason).trim() !== '')
      ? `(reason: ${newV.reason})`
      : '';
    if (!changed.length) return reasonNote || '(no field changes)';
    return reasonNote ? `${changed.join(', ')} ${reasonNote}` : changed.join(', ');
  }
  // Fallback: whichever side is present.
  return summarizeAuditRecord(newV || oldV);
};

// Status chip colors
const getStatusColor = (status) => {
  const colors = {
    'Active': 'success',
    'Approved': 'info',
    'Pending Approval': 'warning',
    'Draft': 'default',
    'Matured': 'secondary',
    'Cancelled': 'error',
  };
  return colors[status] || 'default';
};

function ReportsPage() {
  const { isFeatureEnabled, tenant } = useTenant();
  const { isAdmin, canApprove } = usePermissions();
  const notify = useNotification();
  const dp = tenant?.roundingPrecision ?? 3;

  // Can the current user pay out residual reserve from the suspense report?
  // Mirrors the contract-detail gate (approve on Contract Management).
  const canPayoutReserve = canApprove(MODULES.CONTRACT_MANAGEMENT);

  // Reserve Payout dialog (launched from a suspense report row)
  const [reservePayoutDialogOpen, setReservePayoutDialogOpen] = useState(false);
  const [reservePayoutContract, setReservePayoutContract] = useState(null);

  // Pure-number amount (no currency prefix) — report tables show the currency in
  // its own dedicated column, so amounts render as plain numbers at tenant precision.
  const formatAmt = (amount) => formatNum(amount, dp);
  const [tabValue, setTabValue] = useState(0);

  // Contract Register state
  const [contractFilters, setContractFilters] = useState({
    status: 'all',
    investorId: 'all',      // server-side filter param
    productId: 'all',       // server-side filter param
    issuerId: 'all',        // server-side filter param ('-1' = no issuer linked)
    dateFrom: '',
    dateTo: '',
    search: '',             // client-side search across reference / external ref / investor / issuer / product
  });
  // Export shape: off = one row per contract (totals stay correct under a naive SUM);
  // on = each multi-investor contract followed by its per-investor rows.
  const [contractIncludeBreakdown, setContractIncludeBreakdown] = useState(false);
  // True when the server returned more rows than we display, i.e. the cap truncated.
  const [contractTruncated, setContractTruncated] = useState(false);
  const [contractData, setContractData] = useState([]);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractError, setContractError] = useState(null);
  // Dropdown options for the Contract Register Investor + Product filters.
  const [contractInvestorOptions, setContractInvestorOptions] = useState([]);
  const [contractProductOptions, setContractProductOptions] = useState([]);
  const [contractIssuerOptions, setContractIssuerOptions] = useState([]);
  // Expanded multi-investor rows. The per-investor breakdown ships inline on the
  // register row (contract.investors), so this is pure local state — no fetch.
  const [contractExpanded, setContractExpanded] = useState({});
  const toggleContractExpand = useCallback((contractId) => {
    setContractExpanded(prev => ({ ...prev, [contractId]: !prev[contractId] }));
  }, []);
  // Rows narrowed by the client-side search (used for the table + export + counts).
  const filteredContractData = useMemo(() => {
    const q = contractFilters.search.trim().toLowerCase();
    if (!q) return contractData;
    return contractData.filter(c =>
      [c.reference, c.contract_reference, c.user_reference, c.investor_name, c.issuer_name, c.product_type, c.investment_type, c.currency_code, c.status]
        // Multi-investor contracts aggregate their names into investor_name, but
        // also match on the nested per-investor names so a search still finds a
        // contract by an investor listed only inside its expansion.
        .concat(Array.isArray(c.investors) ? c.investors.map(i => i.investor_name) : [])
        .some(v => (v || '').toString().toLowerCase().includes(q))
    );
  }, [contractData, contractFilters.search]);
  // Rows the export will actually contain, so the button's (N) tells the truth in both
  // modes: contracts only when the breakdown toggle is off, contracts + one row per
  // investor when it is on (68 -> 92 on dev). Must mirror the row-building in
  // exportContractRegister — a multi-investor contract with an empty investors array
  // emits no children.
  const contractExportRowCount = useMemo(() => {
    if (!contractIncludeBreakdown) return filteredContractData.length;
    return filteredContractData.reduce((total, c) => {
      const investors = Array.isArray(c.investors) ? c.investors : [];
      return total + 1 + (c.is_multi_investor && investors.length > 0 ? investors.length : 0);
    }, 0);
  }, [filteredContractData, contractIncludeBreakdown]);

  // Payment Schedule state
  const [paymentFilters, setPaymentFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: 'all',          // client-side filter on the row Status (overdue/due_soon/pending)
    search: '',             // client-side search across contract ref / investor / currency / status
  });
  const [paymentData, setPaymentData] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  // Rows narrowed by the client-side Status + Search filters (used for the table + export + counts).
  const filteredPaymentData = useMemo(() => {
    const q = (paymentFilters.search || '').trim().toLowerCase();
    return paymentData.filter(p => {
      const matchesStatus = paymentFilters.status === 'all' || (p.status || 'pending') === paymentFilters.status;
      const matchesSearch = !q || [p.contract_reference, p.investor_name, p.currency_code, p.status, p.period_number]
        .some(v => (v || '').toString().toLowerCase().includes(q));
      return matchesStatus && matchesSearch;
    });
  }, [paymentData, paymentFilters.status, paymentFilters.search]);

  // Investor Summary state
  const [investorFilters, setInvestorFilters] = useState({
    currency: 'all',        // server-side filter param
    search: '',             // client-side search across name / type / country
    type: 'all',            // client-side filter on investor type
    status: 'all',          // client-side filter on active flag (active/inactive)
  });
  const [investorData, setInvestorData] = useState([]);
  const [investorLoading, setInvestorLoading] = useState(false);
  const [investorError, setInvestorError] = useState(null);
  // Distinct investor types from the loaded rows (for the client-side Type dropdown).
  const investorTypeOptions = useMemo(
    () => [...new Set(investorData.map(i => i.type).filter(Boolean))].sort(),
    [investorData]
  );
  // Rows narrowed by the client-side search / type / status filters.
  const filteredInvestorData = useMemo(() => {
    const q = investorFilters.search.trim().toLowerCase();
    return investorData.filter(i => {
      const matchesSearch = !q || [i.name, i.type, i.domicile_country_name, i.currency_code, i.active ? 'active' : 'inactive']
        .some(v => (v || '').toString().toLowerCase().includes(q));
      const matchesType = investorFilters.type === 'all' || i.type === investorFilters.type;
      const matchesStatus = investorFilters.status === 'all'
        || (investorFilters.status === 'active' ? !!i.active : !i.active);
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [investorData, investorFilters.search, investorFilters.type, investorFilters.status]);

  // Audit Trail state
  const [auditFilters, setAuditFilters] = useState({
    action: 'all',
    tableName: 'all',       // server-side table_name filter (routes to tenant vs RBAC source)
    changedBy: '',          // server-side changed_by filter (ILIKE match on user)
    dateFrom: '',
    dateTo: '',
    search: '',             // client-side search across user / table / record id
  });
  const [auditData, setAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  // Rows narrowed by the client-side search (used for the table + export + counts).
  const filteredAuditData = useMemo(() => {
    const q = auditFilters.search.trim().toLowerCase();
    if (!q) return auditData;
    return auditData.filter(a =>
      [a.changed_by, a.user_email, a.table_name, a.record_id, a.action]
        .some(v => (v || '').toString().toLowerCase().includes(q))
    );
  }, [auditData, auditFilters.search]);
  // Options for the Table filter: the RBAC audit tables (from role_audit_log,
  // surfaced via the merged /admin/audit endpoint) plus whatever table_names the
  // currently loaded rows contain, deduped and sorted.
  const auditTableOptions = useMemo(() => {
    const rbacTables = ['role', 'role_permission', 'user_role_assignment', 'user_override'];
    const seen = new Set(rbacTables);
    auditData.forEach((a) => { if (a.table_name) seen.add(a.table_name); });
    return Array.from(seen).sort();
  }, [auditData]);

  // Current vs Long-Term state
  const [portionFilters, setPortionFilters] = useState({
    asOfDate: '',
    search: '',
    productId: '',
    currencyId: '',
    investor: '',
  });
  const [portionData, setPortionData] = useState([]);
  const [portionLoading, setPortionLoading] = useState(false);
  const [portionError, setPortionError] = useState(null);

  // Rolled Contracts state
  const [rolledFilters, setRolledFilters] = useState({
    dateFrom: '',
    dateTo: '',
    search: '',             // client-side search across orig/new ref + investor
    currency: 'all',        // client-side filter on original currency code
  });
  const [rolledData, setRolledData] = useState([]);
  const [rolledLoading, setRolledLoading] = useState(false);
  const [rolledError, setRolledError] = useState(null);
  // Distinct currencies from the loaded rows (for the client-side Currency dropdown).
  const rolledCurrencyOptions = useMemo(
    () => [...new Set(rolledData.map(r => r.original_currency_code).filter(Boolean))].sort(),
    [rolledData]
  );
  // Rows narrowed by the client-side search / currency filters.
  const filteredRolledData = useMemo(() => {
    const q = rolledFilters.search.trim().toLowerCase();
    return rolledData.filter(r => {
      const matchesSearch = !q || [r.original_contract_ref, r.new_contract_ref, r.original_investor_name,
        r.original_user_ref, r.new_user_ref, r.original_currency_code, r.original_status, r.new_status]
        .some(v => (v || '').toString().toLowerCase().includes(q));
      const matchesCurrency = rolledFilters.currency === 'all'
        || r.original_currency_code === rolledFilters.currency;
      return matchesSearch && matchesCurrency;
    });
  }, [rolledData, rolledFilters.search, rolledFilters.currency]);

  // Rollover Fund Flow state
  const [fundFlowFilters, setFundFlowFilters] = useState({
    dateFrom: '',
    dateTo: '',
    search: '',
    unallocatedOnly: false,
  });
  const [fundFlowData, setFundFlowData] = useState([]);
  const [fundFlowLoading, setFundFlowLoading] = useState(false);
  const [fundFlowError, setFundFlowError] = useState(null);

  // Rollover Suspense state
  const [suspenseFilters, setSuspenseFilters] = useState({
    dateFrom: '',
    dateTo: '',
    search: '',
    unallocatedOnly: false,
  });
  const [suspenseData, setSuspenseData] = useState([]);
  const [suspenseLoading, setSuspenseLoading] = useState(false);
  const [suspenseError, setSuspenseError] = useState(null);

  // The Current-vs-Long-Term / Fund-Flow / Suspense search boxes already send a
  // server `search` (ref / external-ref / investor) on Generate Preview. These
  // useMemos additionally narrow the loaded rows client-side across a broader
  // field set (product / currency / status) so the search behaves like the other
  // tabs; they drive the table render, footer count and Excel export.
  const filteredPortionData = useMemo(() => {
    const q = (portionFilters.search || '').trim().toLowerCase();
    if (!q) return portionData;
    return portionData.filter(r =>
      [r.contract_reference, r.user_reference, r.investor_name, r.product_type, r.currency_code]
        .some(v => (v || '').toString().toLowerCase().includes(q))
    );
  }, [portionData, portionFilters.search]);

  const filteredFundFlowData = useMemo(() => {
    const q = (fundFlowFilters.search || '').trim().toLowerCase();
    if (!q) return fundFlowData;
    return fundFlowData.filter(r =>
      [r.source_contract_ref, r.source_user_ref, r.investor_name, r.currency_code, r.source_status]
        .some(v => (v || '').toString().toLowerCase().includes(q))
    );
  }, [fundFlowData, fundFlowFilters.search]);

  const filteredSuspenseData = useMemo(() => {
    const q = (suspenseFilters.search || '').trim().toLowerCase();
    if (!q) return suspenseData;
    return suspenseData.filter(r =>
      [r.source_contract_ref, r.source_user_ref, r.investor_name, r.currency_code]
        .some(v => (v || '').toString().toLowerCase().includes(q))
    );
  }, [suspenseData, suspenseFilters.search]);

  // Interest Accrual Journal state
  const [journalFilters, setJournalFilters] = useState({
    dateFrom: '',
    dateTo: '',
    site: null,
    overrideSiteForUnlinked: '',
    sil: '',
    department: '',
    reExport: false,
    search: '',             // client-side search — narrows the PREVIEW only (the Excel extract is server-driven)
  });
  const [journalData, setJournalData] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState(null);
  const [journalExporting, setJournalExporting] = useState(false);
  const [journalSiteOptions, setJournalSiteOptions] = useState([]);
  // Preview rows narrowed by the client-side Search (drives the table + footer count
  // only; the Excel extract goes through the backend and is NOT search-filtered).
  const filteredJournalData = useMemo(() => {
    const q = (journalFilters.search || '').trim().toLowerCase();
    if (!q) return journalData;
    return journalData.filter(r =>
      [r.contract_reference, r.user_reference, r.investor_name, r.product_reference, r.product_type,
        r.currency_code, r.narration, r.dr_expense_account, r.cr_control_account]
        .some(v => (v || '').toString().toLowerCase().includes(q))
    );
  }, [journalData, journalFilters.search]);

  // Issuer Ledger state
  const [ledgerFilters, setLedgerFilters] = useState({
    dateFrom: '',
    dateTo: '',
    issuerId: '',
    productId: '',
    search: '',             // client-side search — narrows the PREVIEW only (the Excel extract is server-driven)
  });
  const [ledgerData, setLedgerData] = useState({ rows: [], openings: [] });
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState(null);
  const [ledgerExporting, setLedgerExporting] = useState(false);
  const [ledgerIssuerOptions, setLedgerIssuerOptions] = useState([]);
  const [ledgerProductOptions, setLedgerProductOptions] = useState([]);
  // Source ledger rows narrowed by the client-side Search; fed into the grouping
  // render so issuer/investor/contract headers + per-currency subtotals rebuild
  // over the visible set. Preview only — the Excel extract is server-driven.
  const filteredLedgerRows = useMemo(() => {
    const q = (ledgerFilters.search || '').trim().toLowerCase();
    const rows = ledgerData.rows || [];
    if (!q) return rows;
    return rows.filter(r =>
      [r.contract_reference, r.ext_reference, r.issuer_name, r.investor_name, r.narration,
        r.product_type, r.class, r.gl_account, r.currency_code]
        .some(v => (v || '').toString().toLowerCase().includes(q))
    );
  }, [ledgerData.rows, ledgerFilters.search]);

  // Dropdown data for portion filters (product + currency only)
  const [portionDropdowns, setPortionDropdowns] = useState({
    products: [],
    currencies: [],
    loaded: false,
  });

  const loadPortionDropdowns = async () => {
    if (portionDropdowns.loaded) return;
    try {
      const [productsRes, currenciesRes] = await Promise.all([
        apiGet('/refdata/investment-types?active_only=true'),
        apiGet('/refdata/countries?active_only=true'),
      ]);
      setPortionDropdowns({
        products: (productsRes.data || productsRes || []),
        currencies: (currenciesRes.data || currenciesRes || []),
        loaded: true,
      });
    } catch (err) {
      logger.error('Error loading dropdowns', { message: err.message, status: err.response?.status });
    }
  };

  // Load Contract Register data
  const loadContractData = async () => {
    setContractLoading(true);
    setContractError(null);
    // Drop stale expansion keys so a previous preview's open rows don't persist.
    setContractExpanded({});
    setContractTruncated(false);
    try {
      // Ask for one MORE than we display: if that extra row comes back, the cap has
      // truncated the result and we say so instead of silently showing a short list.
      // Cheaper than a COUNT(*) OVER (), which would add a full scan to every request.
      let url = `/reports/contract-register?limit=${CONTRACT_REGISTER_LIMIT + 1}`;
      if (contractFilters.status !== 'all') {
        url += `&status=${contractFilters.status}`;
      }
      if (contractFilters.investorId !== 'all') {
        url += `&investor_id=${contractFilters.investorId}`;
      }
      if (contractFilters.productId !== 'all') {
        url += `&product_id=${contractFilters.productId}`;
      }
      if (contractFilters.issuerId !== 'all') {
        url += `&issuer_id=${contractFilters.issuerId}`;
      }
      if (contractFilters.dateFrom) {
        url += `&date_from=${contractFilters.dateFrom}`;
      }
      if (contractFilters.dateTo) {
        url += `&date_to=${contractFilters.dateTo}`;
      }

      const response = await apiGet(url, { timeout: REPORT_TIMEOUT });
      const rows = extractApiData(response) || [];
      const truncated = rows.length > CONTRACT_REGISTER_LIMIT;
      setContractTruncated(truncated);
      setContractData(truncated ? rows.slice(0, CONTRACT_REGISTER_LIMIT) : rows);
    } catch (err) {
      logger.error('Error loading contract register', { message: err.message, status: err.response?.status });
      setContractError(describeApiError(err, { action: 'load the contract register' }));
    } finally {
      setContractLoading(false);
    }
  };

  // Load Payment Schedule data
  const loadPaymentData = async () => {
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      let url = '/reports/payment-schedule?limit=500';
      if (paymentFilters.dateFrom) {
        url += `&date_from=${paymentFilters.dateFrom}`;
      }
      if (paymentFilters.dateTo) {
        url += `&date_to=${paymentFilters.dateTo}`;
      }

      const response = await apiGet(url, { timeout: REPORT_TIMEOUT });
      setPaymentData(extractApiData(response));
    } catch (err) {
      logger.error('Error loading payment schedule', { message: err.message, status: err.response?.status });
      setPaymentError(describeApiError(err, { action: 'load the payment schedule' }));
    } finally {
      setPaymentLoading(false);
    }
  };

  // Load Investor Summary data
  const loadInvestorData = async () => {
    setInvestorLoading(true);
    setInvestorError(null);
    try {
      // Full list — Investor Summary searches/filters client-side (filteredInvestorData),
      // so a low limit would hide investors past the cap from the search box.
      const response = await apiGet('/stakeholders/investors?limit=100000');
      setInvestorData(extractApiData(response));
    } catch (err) {
      logger.error('Error loading investor summary', { message: err.message, status: err.response?.status });
      setInvestorError(describeApiError(err, { action: 'load investor data' }));
    } finally {
      setInvestorLoading(false);
    }
  };

  // Load Audit Trail data
  const loadAuditData = async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      let url = '/admin/audit?limit=100000';
      if (auditFilters.action !== 'all') {
        url += `&action=${auditFilters.action}`;
      }
      if (auditFilters.tableName !== 'all') {
        url += `&table_name=${encodeURIComponent(auditFilters.tableName)}`;
      }
      if (auditFilters.changedBy.trim()) {
        url += `&changed_by=${encodeURIComponent(auditFilters.changedBy.trim())}`;
      }
      if (auditFilters.dateFrom) {
        url += `&date_from=${auditFilters.dateFrom}`;
      }
      if (auditFilters.dateTo) {
        url += `&date_to=${auditFilters.dateTo}`;
      }

      const response = await apiGet(url);
      setAuditData(extractApiData(response));
    } catch (err) {
      logger.error('Error loading audit trail', { message: err.message, status: err.response?.status });
      setAuditError(describeApiError(err, { action: 'load audit data' }));
    } finally {
      setAuditLoading(false);
    }
  };

  // Export handlers
  const handleExportContracts = () => {
    if (filteredContractData.length > 0) {
      exportContractRegister(filteredContractData, { includeBreakdown: contractIncludeBreakdown });
    }
  };

  const handleExportPayments = () => {
    if (filteredPaymentData.length > 0) {
      exportPaymentSchedule(filteredPaymentData);
    }
  };

  const handleExportInvestors = () => {
    if (filteredInvestorData.length > 0) {
      exportInvestorSummary(filteredInvestorData);
    }
  };

  const handleExportAudit = () => {
    if (filteredAuditData.length > 0) {
      exportAuditTrail(filteredAuditData);
    }
  };

  // Load Current vs Long-Term data
  const loadPortionData = async () => {
    setPortionLoading(true);
    setPortionError(null);
    setExpandedRows({});
    setDetailData({});
    setDetailLoading({});
    try {
      let url = '/reports/current-long-term?limit=1000';
      if (portionFilters.asOfDate) url += `&as_of_date=${portionFilters.asOfDate}`;
      if (portionFilters.search) url += `&search=${encodeURIComponent(portionFilters.search)}`;
      if (portionFilters.productId) url += `&product_id=${portionFilters.productId}`;
      if (portionFilters.currencyId) url += `&currency_id=${portionFilters.currencyId}`;
      if (portionFilters.investor) url += `&investor=${encodeURIComponent(portionFilters.investor)}`;

      const response = await apiGet(url, { timeout: REPORT_TIMEOUT });
      setPortionData(extractApiData(response));
    } catch (err) {
      logger.error('Error loading current/long-term portions', { message: err.message, status: err.response?.status });
      setPortionError(describeApiError(err, { action: 'load the current vs long-term report' }));
    } finally {
      setPortionLoading(false);
    }
  };

  const handleExportPortions = () => {
    if (filteredPortionData.length > 0) {
      exportCurrentLongTerm(filteredPortionData);
    }
  };

  // Load Rolled Contracts data
  const loadRolledData = async () => {
    setRolledLoading(true);
    setRolledError(null);
    try {
      let url = '/reports/rolled-contracts?limit=1000';
      if (rolledFilters.dateFrom) url += `&date_from=${rolledFilters.dateFrom}`;
      if (rolledFilters.dateTo) url += `&date_to=${rolledFilters.dateTo}`;

      const response = await apiGet(url, { timeout: REPORT_TIMEOUT });
      setRolledData(extractApiData(response));
    } catch (err) {
      logger.error('Error loading rolled contracts', { message: err.message, status: err.response?.status });
      setRolledError(describeApiError(err, { action: 'load the rolled contracts report' }));
    } finally {
      setRolledLoading(false);
    }
  };

  const handleExportRolled = () => {
    if (filteredRolledData.length > 0) {
      exportRolledContracts(filteredRolledData);
    }
  };

  // Load Rollover Fund Flow data
  const loadFundFlowData = async () => {
    setFundFlowLoading(true);
    setFundFlowError(null);
    try {
      let url = '/reports/rollover-fund-flow?limit=1000';
      if (fundFlowFilters.dateFrom) url += `&date_from=${fundFlowFilters.dateFrom}`;
      if (fundFlowFilters.dateTo) url += `&date_to=${fundFlowFilters.dateTo}`;
      if (fundFlowFilters.search) url += `&search=${encodeURIComponent(fundFlowFilters.search)}`;
      if (fundFlowFilters.unallocatedOnly) url += '&unallocated_only=true';

      const response = await apiGet(url, { timeout: REPORT_TIMEOUT });
      setFundFlowData(extractApiData(response));
    } catch (err) {
      logger.error('Error loading rollover fund flow', { message: err.message, status: err.response?.status });
      setFundFlowError(describeApiError(err, { action: 'load the rollover fund flow report' }));
    } finally {
      setFundFlowLoading(false);
    }
  };

  const handleExportFundFlow = () => {
    if (filteredFundFlowData.length > 0) {
      exportRolloverFundFlow(filteredFundFlowData);
    }
  };

  // Load Rollover Suspense data
  const loadSuspenseData = async () => {
    setSuspenseLoading(true);
    setSuspenseError(null);
    try {
      let url = '/reports/contract-suspense?limit=1000';
      if (suspenseFilters.dateFrom) url += `&date_from=${suspenseFilters.dateFrom}`;
      if (suspenseFilters.dateTo) url += `&date_to=${suspenseFilters.dateTo}`;
      if (suspenseFilters.search) url += `&search=${encodeURIComponent(suspenseFilters.search)}`;
      if (suspenseFilters.unallocatedOnly) url += '&unallocated_only=true';

      const response = await apiGet(url, { timeout: REPORT_TIMEOUT });
      setSuspenseData(extractApiData(response));
    } catch (err) {
      logger.error('Error loading rollover suspense', { message: err.message, status: err.response?.status });
      setSuspenseError(describeApiError(err, { action: 'load the rollover suspense report' }));
    } finally {
      setSuspenseLoading(false);
    }
  };

  const handleExportSuspense = () => {
    if (filteredSuspenseData.length > 0) {
      exportContractSuspense(filteredSuspenseData);
    }
  };

  // Load Interest Accrual Journal data (preview)
  const loadJournalData = async () => {
    setJournalLoading(true);
    setJournalError(null);
    try {
      let url = '/reports/interest-accrual-journal?';
      if (journalFilters.dateFrom) url += `date_from=${journalFilters.dateFrom}&`;
      if (journalFilters.dateTo) url += `date_to=${journalFilters.dateTo}&`;
      if (journalFilters.site) url += `site=${encodeURIComponent(journalFilters.site)}&`;
      url += 'include_exported=true';

      const response = await apiGet(url, { timeout: REPORT_TIMEOUT });
      setJournalData(extractApiData(response));
    } catch (err) {
      logger.error('Error loading journal data', { message: err.message, status: err.response?.status });
      setJournalError(describeApiError(err, { action: 'load the interest accrual journal' }));
    } finally {
      setJournalLoading(false);
    }
  };

  // Export Interest Accrual Journal to xlsx (marks entries as exported)
  const handleExportJournal = async () => {
    setJournalExporting(true);
    setJournalError(null);
    try {
      const blob = await apiDownload('/reports/interest-accrual-journal/export', {
        date_from: journalFilters.dateFrom,
        date_to: journalFilters.dateTo,
        site: journalFilters.site,
        override_site_for_unlinked:
          journalFilters.site === '__none__' ? journalFilters.overrideSiteForUnlinked : '',
        sil: journalFilters.sil,
        department: journalFilters.department,
        re_export: journalFilters.reExport,
      }, { timeout: REPORT_TIMEOUT });
      // Trigger browser download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Interest_Accrual_Journal_${journalFilters.dateFrom}_${journalFilters.dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      // Reload preview to show updated exported flags
      loadJournalData();
    } catch (err) {
      logger.error('Error exporting journal', { message: err.message, status: err.response?.status });
      // Note: this is a blob download, so on an HTTP error err.response.data is a
      // Blob (not the JSON error body) — branch on status/code, not the message.
      let msg;
      if (err?.response?.status === 404) {
        msg = 'No unexported accrual entries found for this date range.';
      } else if (err?.code === 'ECONNABORTED') {
        msg = 'The export took too long and timed out. Try a narrower date range, or retry.';
      } else if (!err?.response) {
        msg = 'Lost connection to the server while exporting. Check your network and retry.';
      } else {
        msg = 'Failed to export journal. Please try again.';
      }
      setJournalError(msg);
    } finally {
      setJournalExporting(false);
    }
  };

  // Load Issuer Ledger data (preview). Returns { rows, openings }.
  const loadLedgerData = async () => {
    setLedgerLoading(true);
    setLedgerError(null);
    try {
      let url = '/reports/issuer-ledger?';
      if (ledgerFilters.dateFrom) url += `date_from=${ledgerFilters.dateFrom}&`;
      if (ledgerFilters.dateTo) url += `date_to=${ledgerFilters.dateTo}&`;
      if (ledgerFilters.issuerId) url += `issuer_id=${ledgerFilters.issuerId}&`;
      if (ledgerFilters.productId) url += `product_id=${ledgerFilters.productId}&`;

      // The endpoint returns a plain { rows, openings } body (no success/data
      // wrapper), and apiGet already unwraps to response.data — so use it
      // directly rather than running it through extractApiData (which expects
      // the wrapped shape and would yield []).
      const data = await apiGet(url, { timeout: REPORT_TIMEOUT }) || {};
      setLedgerData({ rows: data.rows || [], openings: data.openings || [] });
    } catch (err) {
      logger.error('Error loading issuer ledger', { message: err.message, status: err.response?.status });
      setLedgerError(describeApiError(err, { action: 'load the sub ledger' }));
    } finally {
      setLedgerLoading(false);
    }
  };

  // Export Issuer Ledger to xlsx
  const handleExportLedger = async () => {
    setLedgerExporting(true);
    setLedgerError(null);
    try {
      const blob = await apiDownload('/reports/issuer-ledger/export', {
        date_from: ledgerFilters.dateFrom,
        date_to: ledgerFilters.dateTo,
        issuer_id: ledgerFilters.issuerId || null,
        product_id: ledgerFilters.productId || null,
      }, { timeout: REPORT_TIMEOUT });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Issuer_Ledger_${ledgerFilters.dateFrom}_${ledgerFilters.dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Error exporting issuer ledger', { message: err.message, status: err.response?.status });
      let msg;
      if (err?.response?.status === 404) {
        msg = 'No ledger entries found for this date range.';
      } else if (err?.code === 'ECONNABORTED') {
        msg = 'The export took too long and timed out. Try a narrower date range, or retry.';
      } else if (!err?.response) {
        msg = 'Lost connection to the server while exporting. Check your network and retry.';
      } else {
        msg = 'Failed to export ledger. Please try again.';
      }
      setLedgerError(msg);
    } finally {
      setLedgerExporting(false);
    }
  };

  // Drill-down state for Current vs Long-Term
  const [expandedRows, setExpandedRows] = useState({});
  const [detailData, setDetailData] = useState({});
  const [detailLoading, setDetailLoading] = useState({});

  const toggleRowExpand = useCallback(async (contractId, investorId) => {
    const key = `${contractId}-${investorId || 'all'}`;
    if (expandedRows[key]) {
      setExpandedRows(prev => ({ ...prev, [key]: false }));
      return;
    }

    setExpandedRows(prev => ({ ...prev, [key]: true }));

    // Only fetch if we don't already have data
    if (!detailData[key]) {
      setDetailLoading(prev => ({ ...prev, [key]: true }));
      try {
        let url = `/reports/current-long-term/${contractId}/detail`;
        const params = [];
        if (investorId) params.push(`investor_id=${investorId}`);
        if (portionFilters.asOfDate) params.push(`as_of_date=${portionFilters.asOfDate}`);
        if (params.length) url += '?' + params.join('&');

        const response = await apiGet(url, { timeout: REPORT_TIMEOUT });
        setDetailData(prev => ({ ...prev, [key]: extractApiData(response) }));
      } catch (err) {
        logger.error('Error loading current/long-term detail', { message: err.message, status: err.response?.status });
        setDetailData(prev => ({ ...prev, [key]: [] }));
      } finally {
        setDetailLoading(prev => ({ ...prev, [key]: false }));
      }
    }
  }, [expandedRows, detailData, portionFilters.asOfDate]);

  // Drill-down state for Investor Summary — expands an investor row to the
  // contracts that make up the grouping (reuses the contract-register endpoint).
  const [investorExpanded, setInvestorExpanded] = useState({});
  const [investorContracts, setInvestorContracts] = useState({});
  const [investorContractsLoading, setInvestorContractsLoading] = useState({});

  const toggleInvestorExpand = useCallback(async (investorId) => {
    if (investorExpanded[investorId]) {
      setInvestorExpanded(prev => ({ ...prev, [investorId]: false }));
      return;
    }
    setInvestorExpanded(prev => ({ ...prev, [investorId]: true }));

    if (!investorContracts[investorId]) {
      setInvestorContractsLoading(prev => ({ ...prev, [investorId]: true }));
      try {
        const response = await apiGet(`/reports/contract-register?investor_id=${investorId}&limit=500`, { timeout: REPORT_TIMEOUT });
        setInvestorContracts(prev => ({ ...prev, [investorId]: extractApiData(response) }));
      } catch (err) {
        logger.error('Error loading investor contracts', { message: err.message, status: err.response?.status });
        setInvestorContracts(prev => ({ ...prev, [investorId]: [] }));
      } finally {
        setInvestorContractsLoading(prev => ({ ...prev, [investorId]: false }));
      }
    }
  }, [investorExpanded, investorContracts]);

  // Sub-feature tab filtering
  const allReportTabs = useMemo(() => [
    { feature: 'contract_register', label: 'Contract Register', description: 'Register of contracts with capital, rate, dates and status — filterable by status and date range.' },
    { feature: 'payment_schedule', label: 'Payment Schedule', description: 'Scheduled capital and interest payments across contracts for a given date range and status.' },
    { feature: 'investor_summary', label: 'Investor Summary', description: 'Per-investor summary of holdings and balances, optionally filtered by currency.' },
    { feature: 'current_long_term', label: 'Current vs Long-Term', description: 'Split of outstanding balances into current (due within 12 months) and long-term portions as at a chosen date.' },
    { feature: 'rolled_contracts', label: 'Rolled Contracts', description: 'Audit report showing contract rollover chains — which contracts were rolled into new contracts, with amounts and dates.' },
    { feature: 'rollover_fund_flow', label: 'Rollover Fund Flow', description: 'Per-investor breakdown of how capital was allocated during rollovers — rolled into new contracts, paid out, or remaining unallocated.' },
    { feature: 'contract_suspense', label: 'Rollover Suspense', description: 'Per-investor breakdown of funds held in rollover suspense after contract finalisation — showing how much has been allocated to new contracts and what remains.' },
    { feature: 'interest_accrual_journal', label: 'Interest Accrual Journal', description: 'Interest accrual journal entries for export to the GL, with tracking of previously exported entries and re-export.' },
    { feature: 'issuer_ledger', label: 'Sub Ledger', description: 'Dr/Cr creditors sub-ledger of contract movements, grouped Issuer → Investor → Contract. Each schedule line is split into separate Capital / Interest / WHT rows.' },
    { feature: 'audit_trail', label: 'Audit Trail', description: 'Chronological audit log of changes across the system, filterable by date and entity.' },
  ], []);

  const visibleReportTabs = useMemo(() =>
    allReportTabs.filter(t =>
      isFeatureEnabled('reports', t.feature) &&
      (t.feature !== 'audit_trail' || isAdmin)
    ),
    [allReportTabs, isFeatureEnabled, isAdmin]
  );

  const safeTabValue = visibleReportTabs.length > 0 ? Math.min(tabValue, visibleReportTabs.length - 1) : 0;
  const activeReportFeature = visibleReportTabs[safeTabValue]?.feature;

  // Load site dropdown options when the journal section becomes visible
  useEffect(() => {
    if (activeReportFeature !== 'interest_accrual_journal') return;
    if (journalSiteOptions.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/reports/interest-accrual-journal/sites');
        const rows = extractApiData(res);
        const sites = Array.isArray(rows) ? rows.map(r => r.site).filter(Boolean) : [];
        if (!cancelled) setJournalSiteOptions(sites);
      } catch (err) {
        logger.error('Error loading journal site options', { message: err.message, status: err.response?.status });
      }
    })();
    return () => { cancelled = true; };
  }, [activeReportFeature, journalSiteOptions.length]);

  // Load issuer + product dropdowns when the Issuer Ledger section becomes visible
  useEffect(() => {
    if (activeReportFeature !== 'issuer_ledger') return;
    if (ledgerIssuerOptions.length > 0 || ledgerProductOptions.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const [issuersRes, productsRes] = await Promise.all([
          apiGet('/stakeholders/issuers/select?active_only=true'),
          apiGet('/refdata/investment-types?active_only=true'),
        ]);
        if (cancelled) return;
        setLedgerIssuerOptions(extractApiData(issuersRes) || []);
        setLedgerProductOptions(extractApiData(productsRes) || []);
      } catch (err) {
        logger.error('Error loading issuer ledger dropdowns', { message: err.message, status: err.response?.status });
      }
    })();
    return () => { cancelled = true; };
  }, [activeReportFeature, ledgerIssuerOptions.length, ledgerProductOptions.length]);

  // Load investor + product + issuer dropdowns when the Contract Register becomes visible
  useEffect(() => {
    if (activeReportFeature !== 'contract_register') return;
    if (contractInvestorOptions.length > 0 || contractProductOptions.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const [investorsRes, productsRes, issuersRes] = await Promise.all([
          apiGet('/stakeholders/investors/select?active_only=true'),
          apiGet('/refdata/investment-types?active_only=true'),
          apiGet('/stakeholders/issuers/select?active_only=true'),
        ]);
        if (cancelled) return;
        setContractInvestorOptions(extractApiData(investorsRes) || []);
        setContractProductOptions(extractApiData(productsRes) || []);
        setContractIssuerOptions(extractApiData(issuersRes) || []);
      } catch (err) {
        logger.error('Error loading contract register dropdowns', { message: err.message, status: err.response?.status });
      }
    })();
    return () => { cancelled = true; };
  }, [activeReportFeature, contractInvestorOptions.length, contractProductOptions.length]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reports & Analytics
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={safeTabValue} onChange={(e, v) => setTabValue(v)}>
          {visibleReportTabs.map(t => (
            <Tab
              key={t.feature}
              label={
                t.description ? (
                  <Tooltip title={t.description} arrow placement="bottom">
                    <span>{t.label}</span>
                  </Tooltip>
                ) : t.label
              }
            />
          ))}
        </Tabs>
      </Paper>
      {/* Contract Register */}
      {activeReportFeature === 'contract_register' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 6, md: 1.25 }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={contractFilters.dateFrom}
                  onChange={(e) => setContractFilters({ ...contractFilters, dateFrom: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 1.25 }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={contractFilters.dateTo}
                  onChange={(e) => setContractFilters({ ...contractFilters, dateTo: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={contractFilters.status}
                    onChange={(e) => setContractFilters({ ...contractFilters, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="all">All Contracts</MenuItem>
                    <MenuItem value="Active">Active Only</MenuItem>
                    <MenuItem value="Matured">Matured</MenuItem>
                    <MenuItem value="Draft">Drafts</MenuItem>
                    <MenuItem value="Pending Approval">Pending Approval</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 1 }}>
                <Autocomplete
                  options={['all', ...contractInvestorOptions.map((opt) => opt.id)]}
                  value={contractFilters.investorId}
                  onChange={(_, newValue) => setContractFilters({ ...contractFilters, investorId: newValue == null ? 'all' : newValue })}
                  getOptionLabel={(option) => {
                    if (option === 'all' || option == null) return 'All Investors';
                    return contractInvestorOptions.find((opt) => opt.id === option)?.name || '';
                  }}
                  isOptionEqualToValue={(option, val) => option === val}
                  renderInput={(params) => (
                    <TextField {...params} label="Investor" placeholder="Search..." />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1 }}>
                <Autocomplete
                  options={['all', ...contractProductOptions.map((opt) => opt.id)]}
                  value={contractFilters.productId}
                  onChange={(_, newValue) => setContractFilters({ ...contractFilters, productId: newValue == null ? 'all' : newValue })}
                  getOptionLabel={(option) => {
                    if (option === 'all' || option == null) return 'All Products';
                    return contractProductOptions.find((opt) => opt.id === option)?.type || '';
                  }}
                  isOptionEqualToValue={(option, val) => option === val}
                  renderInput={(params) => (
                    <TextField {...params} label="Product" placeholder="Search..." />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1 }}>
                {/* '-1' is the no-issuer sentinel (see fn_report_contract_register).
                    It is listed first because on UAT 52 of 86 contracts have no issuer
                    linked -- the largest single group, not an edge case. */}
                <Autocomplete
                  options={['all', -1, ...contractIssuerOptions.map((opt) => opt.id)]}
                  value={contractFilters.issuerId}
                  onChange={(_, newValue) => setContractFilters({ ...contractFilters, issuerId: newValue == null ? 'all' : newValue })}
                  getOptionLabel={(option) => {
                    if (option === 'all' || option == null) return 'All Issuers';
                    if (option === -1) return '(No Issuer)';
                    return contractIssuerOptions.find((opt) => opt.id === option)?.name || '';
                  }}
                  isOptionEqualToValue={(option, val) => option === val}
                  renderInput={(params) => (
                    <TextField {...params} label="Issuer" placeholder="Search..." />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Ref / investor / issuer / product / currency / status..."
                  value={contractFilters.search}
                  onChange={(e) => setContractFilters({ ...contractFilters, search: e.target.value })}
                />
              </Grid>
              {/* Export shape only — the on-screen table is unaffected (its chevron
                  already reveals the breakdown on demand). Off by default so a naive
                  SUM over a money column in the exported file stays correct.
                  Laid out inline to match the Interest Accrual Journal's
                  "Incl. exported" switch, so the two read as one family. */}
              <Grid size={{ xs: 12, md: 1.25 }} sx={{ display: 'flex', alignItems: 'center', minHeight: '56px' }}>
                <Tooltip title="Include a row per investor for multi-investor contracts in the Excel export (the on-screen table is unaffected)" arrow>
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Switch
                        checked={contractIncludeBreakdown}
                        onChange={(e) => setContractIncludeBreakdown(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2" noWrap>Incl. breakdown</Typography>}
                  />
                </Tooltip>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={contractLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadContractData}
                  sx={{ height: '56px' }}
                  disabled={contractLoading}
                >
                  {contractLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportContracts}
                  sx={{ height: '56px' }}
                  disabled={filteredContractData.length === 0}
                >
                  Export Excel ({contractExportRowCount})
                </Button>
              </Grid>
            </Grid>

            {contractError && (
              <Alert severity="error" sx={{ mb: 2 }}>{contractError}</Alert>
            )}

            {contractTruncated && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Showing the first {CONTRACT_REGISTER_LIMIT} contracts — more matched than can be
                displayed, so this list and its export are incomplete. Narrow the filters
                (status, date range, investor, product or issuer) to see the rest.
              </Alert>
            )}

            {contractData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Reference</TableCell>
                      <TableCell>External Ref</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Issuer</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Rate %</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Maturity Date</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Capital Amount</TableCell>
                      <TableCell align="right">Total Interest</TableCell>
                      <TableCell align="right">Total Contract Value</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredContractData.map((contract) => {
                      const investors = Array.isArray(contract.investors) ? contract.investors : [];
                      // Only multi-investor contracts have a breakdown worth opening.
                      const canExpand = contract.is_multi_investor && investors.length > 0;
                      const isExpanded = !!contractExpanded[contract.id];
                      return (
                      <React.Fragment key={contract.id}>
                      <TableRow hover>
                        <TableCell padding="checkbox">
                          {canExpand && (
                            <IconButton size="small" onClick={() => toggleContractExpand(contract.id)}>
                              {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>{contract.reference || contract.contract_reference}</TableCell>
                        <TableCell>{contract.user_reference}</TableCell>
                        <TableCell>
                          {contract.is_multi_investor ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={contract.investor_name || ''}>
                                <Chip
                                  icon={<PeopleIcon />}
                                  label={`Multiple (${contract.investor_count ?? 0})`}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              </Tooltip>
                              {contract.pending_investor_count > 0 && (
                                <Tooltip title={`${contract.pending_investor_count} investor allocation(s) awaiting approval of the rolling contract`}>
                                  <Chip label="Pending" size="small" color="warning" variant="outlined" />
                                </Tooltip>
                              )}
                            </Box>
                          ) : (
                            contract.investor_name
                          )}
                        </TableCell>
                        <TableCell>{contract.issuer_name}</TableCell>
                        <TableCell>{contract.product_type || contract.investment_type}</TableCell>
                        <TableCell align="right">{contract.interest_rate}%</TableCell>
                        <TableCell>{formatDate(contract.deposit_date)}</TableCell>
                        <TableCell>{formatDate(contract.maturity_date)}</TableCell>
                        <TableCell>{contract.currency_code}</TableCell>
                        <TableCell align="right">{formatAmt(contract.capital_amount)}</TableCell>
                        <TableCell align="right">{formatAmt(contract.total_interest)}</TableCell>
                        <TableCell align="right">{formatAmt(contract.total_contract_value)}</TableCell>
                        <TableCell>
                          <Chip
                            label={contract.status}
                            size="small"
                            color={getStatusColor(contract.status)}
                          />
                        </TableCell>
                      </TableRow>
                      {canExpand && (
                        <TableRow>
                          <TableCell sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={14}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ m: 1, ml: 6 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Investor breakdown
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Investor</TableCell>
                                      <TableCell align="right">Capital</TableCell>
                                      <TableCell align="right">Share %</TableCell>
                                      <TableCell align="right">Total Interest</TableCell>
                                      <TableCell align="right">Total Value</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {investors.map((inv) => (
                                      <TableRow key={inv.investor_id}>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {inv.investor_name}
                                            {inv.pending_from_contract_id && (
                                              <Tooltip title={`Pending approval of rolling contract ${inv.pending_from_reference || ''}`.trim()}>
                                                <Chip label="Pending" size="small" color="warning" variant="outlined" />
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right">{formatAmt(inv.capital_amount)}</TableCell>
                                        <TableCell align="right">{inv.percentage != null ? `${inv.percentage}%` : ''}</TableCell>
                                        <TableCell align="right">
                                          {contract.interest_is_per_investor ? (
                                            formatAmt(inv.total_interest)
                                          ) : (
                                            // Never render 0 here: this contract's accrual entries
                                            // carry no investor, so the split is unknown, not zero.
                                            <Tooltip title="This contract's interest entries predate per-investor attribution, so interest cannot be split by investor. The contract total is shown on the row above.">
                                              <Typography variant="body2" color="text.secondary">—</Typography>
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                        <TableCell align="right">
                                          {contract.interest_is_per_investor ? (
                                            formatAmt((parseFloat(inv.capital_amount) || 0) + (parseFloat(inv.total_interest) || 0))
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    {/* No subtotal row: the contract row directly above the
                                        breakdown already carries these totals, so repeating
                                        them here is pure duplication. */}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load contract data
                </Typography>
              </Paper>
            )}

            {contractData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredContractData.length} contract(s)
                {filteredContractData.length !== contractData.length ? ` (filtered from ${contractData.length})` : ''}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Payment Schedule */}
      {activeReportFeature === 'payment_schedule' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={paymentFilters.dateFrom}
                  onChange={(e) => setPaymentFilters({ ...paymentFilters, dateFrom: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={paymentFilters.dateTo}
                  onChange={(e) => setPaymentFilters({ ...paymentFilters, dateTo: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={paymentFilters.status}
                    onChange={(e) => setPaymentFilters({ ...paymentFilters, status: e.target.value })}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="due_soon">Due soon</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Ref / investor / currency / status..."
                  value={paymentFilters.search}
                  onChange={(e) => setPaymentFilters({ ...paymentFilters, search: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={paymentLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadPaymentData}
                  sx={{ height: '56px' }}
                  disabled={paymentLoading}
                >
                  {paymentLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportPayments}
                  sx={{ height: '56px' }}
                  disabled={filteredPaymentData.length === 0}
                >
                  Export Excel ({filteredPaymentData.length})
                </Button>
              </Grid>
            </Grid>

            {paymentError && (
              <Alert severity="error" sx={{ mb: 2 }}>{paymentError}</Alert>
            )}

            {paymentData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Contract Ref</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Payment Date</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Interest</TableCell>
                      <TableCell align="right">Capital</TableCell>
                      <TableCell align="right">Total Payment</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPaymentData.map((payment, index) => (
                      <TableRow key={payment.id || index} hover>
                        <TableCell>{payment.contract_reference}</TableCell>
                        <TableCell>{payment.investor_name || '-'}</TableCell>
                        <TableCell>{formatDate(payment.payment_date)}</TableCell>
                        <TableCell>{payment.period_number}</TableCell>
                        <TableCell>{payment.currency_code}</TableCell>
                        <TableCell align="right">{formatAmt(payment.interest_amount)}</TableCell>
                        <TableCell align="right">{formatAmt(payment.capital_amount)}</TableCell>
                        <TableCell align="right">{formatAmt(payment.payment_amount)}</TableCell>
                        <TableCell align="right">{formatAmt(payment.closing_balance)}</TableCell>
                        <TableCell>
                          <Chip
                            label={{
                              overdue: 'Overdue', due_soon: 'Due soon', pending: 'Pending', paid: 'Paid',
                            }[payment.status] || payment.status || 'Pending'}
                            size="small"
                            color={
                              payment.status === 'paid' ? 'success' :
                              payment.status === 'overdue' ? 'error' :
                              payment.status === 'due_soon' ? 'warning' : 'default'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load payment schedule
                </Typography>
              </Paper>
            )}

            {paymentData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredPaymentData.length} payment(s)
                {filteredPaymentData.length !== paymentData.length ? ` (filtered from ${paymentData.length})` : ''}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Investor Summary */}
      {activeReportFeature === 'investor_summary' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={investorFilters.currency}
                    onChange={(e) => setInvestorFilters({ ...investorFilters, currency: e.target.value })}
                    label="Currency"
                  >
                    <MenuItem value="all">All Currencies</MenuItem>
                    <MenuItem value="ZAR">ZAR</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="BWP">BWP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={investorFilters.type}
                    onChange={(e) => setInvestorFilters({ ...investorFilters, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    {investorTypeOptions.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={investorFilters.status}
                    onChange={(e) => setInvestorFilters({ ...investorFilters, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Name / type / country / currency / status..."
                  value={investorFilters.search}
                  onChange={(e) => setInvestorFilters({ ...investorFilters, search: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={investorLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadInvestorData}
                  sx={{ height: '56px' }}
                  disabled={investorLoading}
                >
                  {investorLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportInvestors}
                  sx={{ height: '56px' }}
                  disabled={filteredInvestorData.length === 0}
                >
                  Export Excel ({filteredInvestorData.length})
                </Button>
              </Grid>
            </Grid>

            {investorError && (
              <Alert severity="error" sx={{ mb: 2 }}>{investorError}</Alert>
            )}

            {investorData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Investor Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Total Contracts</TableCell>
                      <TableCell align="right">Active Contracts</TableCell>
                      <TableCell align="right">Total Capital</TableCell>
                      <TableCell align="right">Total Interest</TableCell>
                      <TableCell align="right">Total Contract Value</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInvestorData.map((investor) => {
                      const isExpanded = !!investorExpanded[investor.id];
                      const childRows = investorContracts[investor.id] || [];
                      const childLoading = !!investorContractsLoading[investor.id];
                      return (
                      <React.Fragment key={investor.id}>
                      <TableRow hover sx={{ cursor: 'pointer', ...(isExpanded ? { backgroundColor: 'action.hover' } : {}) }} onClick={() => toggleInvestorExpand(investor.id)}>
                        <TableCell padding="checkbox">
                          <IconButton size="small">
                            {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{investor.name}</TableCell>
                        <TableCell>{investor.type}</TableCell>
                        <TableCell>{investor.domicile_country_name}</TableCell>
                        <TableCell>{investor.currency_code}</TableCell>
                        <TableCell align="right">{investor.total_contracts || 0}</TableCell>
                        <TableCell align="right">{investor.active_contracts || 0}</TableCell>
                        <TableCell align="right">{formatAmt(investor.total_capital)}</TableCell>
                        <TableCell align="right">{formatAmt(investor.total_interest)}</TableCell>
                        <TableCell align="right">{formatAmt(investor.total_contract_value)}</TableCell>
                        <TableCell>
                          <Chip
                            label={investor.active ? 'Active' : 'Inactive'}
                            size="small"
                            color={investor.active ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={11}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ m: 1 }}>
                              {childLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : childRows.length === 0 ? (
                                <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
                                  No contracts found for this investor.
                                </Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Reference</TableCell>
                                      <TableCell>External Ref</TableCell>
                                      <TableCell>Product</TableCell>
                                      <TableCell>Currency</TableCell>
                                      <TableCell align="right">Rate %</TableCell>
                                      <TableCell>Start Date</TableCell>
                                      <TableCell>Maturity Date</TableCell>
                                      <TableCell align="right">Capital</TableCell>
                                      <TableCell align="right">Total Interest</TableCell>
                                      <TableCell align="right">Total Contract Value</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {childRows.map((c) => (
                                      <TableRow key={c.id} hover>
                                        <TableCell>{c.reference}</TableCell>
                                        <TableCell>{c.user_reference}</TableCell>
                                        <TableCell>{c.product_type}</TableCell>
                                        <TableCell>{c.currency_code}</TableCell>
                                        <TableCell align="right">{c.interest_rate}%</TableCell>
                                        <TableCell>{formatDate(c.deposit_date)}</TableCell>
                                        <TableCell>{formatDate(c.maturity_date)}</TableCell>
                                        <TableCell align="right">{formatAmt(c.capital_amount)}</TableCell>
                                        <TableCell align="right">{formatAmt(c.total_interest)}</TableCell>
                                        <TableCell align="right">{formatAmt(c.total_contract_value)}</TableCell>
                                        <TableCell>
                                          <Chip label={c.status} size="small" color={getStatusColor(c.status)} />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load investor summary
                </Typography>
              </Paper>
            )}

            {investorData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredInvestorData.length} investor(s)
                {filteredInvestorData.length !== investorData.length ? ` (filtered from ${investorData.length})` : ''}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Current vs Long-Term Portions */}
      {activeReportFeature === 'current_long_term' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="As-Of Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={portionFilters.asOfDate}
                  onChange={(e) => setPortionFilters({ ...portionFilters, asOfDate: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="Investor"
                  placeholder="Name..."
                  value={portionFilters.investor}
                  onChange={(e) => setPortionFilters({ ...portionFilters, investor: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && loadPortionData()}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Product</InputLabel>
                  <Select
                    value={portionFilters.productId}
                    onChange={(e) => setPortionFilters({ ...portionFilters, productId: e.target.value })}
                    label="Product"
                    onOpen={loadPortionDropdowns}
                  >
                    <MenuItem value="">All</MenuItem>
                    {portionDropdowns.products.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={portionFilters.currencyId}
                    onChange={(e) => setPortionFilters({ ...portionFilters, currencyId: e.target.value })}
                    label="Currency"
                    onOpen={loadPortionDropdowns}
                  >
                    <MenuItem value="">All</MenuItem>
                    {[...new Map(portionDropdowns.currencies.map(c => [c.currency_code, c])).values()]
                      .filter(c => c.currency_code)
                      .map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.currency_code}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Ref / ext ref / investor / product / currency..."
                  value={portionFilters.search}
                  onChange={(e) => setPortionFilters({ ...portionFilters, search: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && loadPortionData()}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={portionLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadPortionData}
                  sx={{ height: '56px' }}
                  disabled={portionLoading}
                >
                  {portionLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportPortions}
                  sx={{ height: '56px' }}
                  disabled={filteredPortionData.length === 0}
                >
                  Export Excel ({filteredPortionData.length})
                </Button>
              </Grid>
            </Grid>

            {portionError && (
              <Alert severity="error" sx={{ mb: 2 }}>{portionError}</Alert>
            )}

            {portionData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell>Reference</TableCell>
                      <TableCell>Ext. Reference</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>CCY</TableCell>
                      <TableCell align="right">Capital</TableCell>
                      <TableCell align="right">Rate %</TableCell>
                      <TableCell>Deposit</TableCell>
                      <TableCell>Maturity</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>Paid Principal</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>Paid Interest</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#f5f5f5' }}>Paid WHT</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Paid Total</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Current Principal</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Current Interest</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Current WHT</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>Current Total</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#fff3e0' }}>LT Principal</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#fff3e0' }}>LT Interest</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#fff3e0' }}>LT WHT</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>LT Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Outstanding</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPortionData.map((row, index) => {
                      const rowKey = `${row.contract_id}-${row.investor_id || 'all'}`;
                      const isExpanded = expandedRows[rowKey];
                      const entries = detailData[rowKey] || [];
                      const isDetailLoading = detailLoading[rowKey];
                      return (
                        <React.Fragment key={`${row.contract_id}-${index}`}>
                          <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleRowExpand(row.contract_id, row.investor_id)}>
                            <TableCell sx={{ width: 40, p: 0.5 }}>
                              <IconButton size="small">
                                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>{row.contract_reference}</TableCell>
                            <TableCell>{row.user_reference || '-'}</TableCell>
                            <TableCell>{row.investor_name}</TableCell>
                            <TableCell>{row.product_type || '-'}</TableCell>
                            <TableCell>{row.currency_code}</TableCell>
                            <TableCell align="right">{formatNum(row.capital_amount, 2)}</TableCell>
                            <TableCell align="right">{row.interest_rate}%</TableCell>
                            <TableCell>{formatDate(row.deposit_date)}</TableCell>
                            <TableCell>{formatDate(row.maturity_date)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fafafa' }}>{formatNum(row.paid_principal, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fafafa' }}>{formatNum(row.paid_interest, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fafafa' }}>{formatNum(row.paid_wht, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>{formatNum(row.paid_total, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f0f7ff' }}>{formatNum(row.current_principal, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f0f7ff' }}>{formatNum(row.current_interest, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f0f7ff' }}>{formatNum(row.current_wht, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f0f7ff', fontWeight: 'bold' }}>{formatNum(row.current_total, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8f0' }}>{formatNum(row.long_term_principal, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8f0' }}>{formatNum(row.long_term_interest, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8f0' }}>{formatNum(row.long_term_wht, 2)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8f0', fontWeight: 'bold' }}>{formatNum(row.long_term_total, 2)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNum(row.total_outstanding, 2)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={23}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ m: 1, ml: 6 }}>
                                  {isDetailLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                                      <CircularProgress size={16} />
                                      <Typography variant="body2">Loading schedule entries...</Typography>
                                    </Box>
                                  ) : entries.length > 0 ? (
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Payment Date</TableCell>
                                          <TableCell>Narration</TableCell>
                                          <TableCell>Bucket</TableCell>
                                          <TableCell align="right">Capital</TableCell>
                                          <TableCell align="right">Interest</TableCell>
                                          <TableCell align="right">WHT</TableCell>
                                          <TableCell align="right">Payment</TableCell>
                                          <TableCell>Status</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {entries.map((entry) => (
                                          <TableRow key={entry.schedule_id}>
                                            <TableCell>{formatDate(entry.payment_date)}</TableCell>
                                            <TableCell>{entry.narration}</TableCell>
                                            <TableCell>
                                              <Chip
                                                label={entry.bucket}
                                                size="small"
                                                color={entry.bucket === 'Paid' ? 'default' : entry.bucket === 'Current' ? 'info' : 'warning'}
                                              />
                                            </TableCell>
                                            <TableCell align="right">{formatNum(entry.capital_amount, 2)}</TableCell>
                                            <TableCell align="right">{formatNum(entry.interest_amount, 2)}</TableCell>
                                            <TableCell align="right">{formatNum(entry.wht_amount, 2)}</TableCell>
                                            <TableCell align="right">{formatNum(entry.payment_amount, 2)}</TableCell>
                                            <TableCell>
                                              {entry.is_paid && <Chip label="Matched" size="small" color="success" />}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
                                      No schedule entries found
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load current vs long-term portions
                </Typography>
              </Paper>
            )}

            {portionData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredPortionData.length} contract(s){filteredPortionData.length !== portionData.length ? ` (filtered from ${portionData.length})` : ''} - Current portion: next 12 months from {portionFilters.asOfDate ? formatDate(portionFilters.asOfDate) : 'start of current month'}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Rolled Contracts */}
      {activeReportFeature === 'rolled_contracts' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  label="From Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={rolledFilters.dateFrom}
                  onChange={(e) => setRolledFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  label="To Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={rolledFilters.dateTo}
                  onChange={(e) => setRolledFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={rolledFilters.currency}
                    onChange={(e) => setRolledFilters(prev => ({ ...prev, currency: e.target.value }))}
                    label="Currency"
                  >
                    <MenuItem value="all">All</MenuItem>
                    {rolledCurrencyOptions.map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  label="Search"
                  fullWidth
                  placeholder="Ref / ext ref / investor / currency / status..."
                  value={rolledFilters.search}
                  onChange={(e) => setRolledFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={rolledLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadRolledData}
                  sx={{ height: '56px' }}
                  disabled={rolledLoading}
                >
                  {rolledLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportRolled}
                  sx={{ height: '56px' }}
                  disabled={filteredRolledData.length === 0}
                >
                  Export Excel ({filteredRolledData.length})
                </Button>
              </Grid>
            </Grid>

            {rolledError && <Alert severity="error" sx={{ mb: 2 }}>{rolledError}</Alert>}

            {rolledData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Original Ref</TableCell>
                      <TableCell>Original Ext Ref</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell align="right">Original Capital</TableCell>
                      <TableCell>CCY</TableCell>
                      <TableCell>Original Maturity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>New Ref</TableCell>
                      <TableCell>New Ext Ref</TableCell>
                      <TableCell align="right">Rolled Amount</TableCell>
                      <TableCell align="right">New Funds</TableCell>
                      <TableCell align="right">New Capital</TableCell>
                      <TableCell>New Deposit</TableCell>
                      <TableCell>New Maturity</TableCell>
                      <TableCell>New Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRolledData.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{row.original_contract_ref}</TableCell>
                        <TableCell>{row.original_user_ref || '-'}</TableCell>
                        <TableCell>{row.original_investor_name}</TableCell>
                        <TableCell align="right">{formatAmt(row.original_capital_amount)}</TableCell>
                        <TableCell>{row.original_currency_code}</TableCell>
                        <TableCell>{formatDate(row.original_maturity_date)}</TableCell>
                        <TableCell>
                          <Chip label={row.original_status} size="small" color={getStatusColor(row.original_status)} />
                        </TableCell>
                        <TableCell>{row.new_contract_ref}</TableCell>
                        <TableCell>{row.new_user_ref || '-'}</TableCell>
                        <TableCell align="right">{formatAmt(row.rolled_amount)}</TableCell>
                        <TableCell align="right">{formatAmt(row.new_funds_amount)}</TableCell>
                        <TableCell align="right">{formatAmt(row.new_capital_amount)}</TableCell>
                        <TableCell>{formatDate(row.new_deposit_date)}</TableCell>
                        <TableCell>{formatDate(row.new_maturity_date)}</TableCell>
                        <TableCell>
                          <Chip label={row.new_status} size="small" color={getStatusColor(row.new_status)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load rolled contracts data
                </Typography>
              </Paper>
            )}

            {rolledData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredRolledData.length} rolled contract record(s)
                {filteredRolledData.length !== rolledData.length ? ` (filtered from ${rolledData.length})` : ''}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Rollover Fund Flow */}
      {activeReportFeature === 'rollover_fund_flow' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  label="From Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={fundFlowFilters.dateFrom}
                  onChange={(e) => setFundFlowFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  label="To Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={fundFlowFilters.dateTo}
                  onChange={(e) => setFundFlowFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  label="Search"
                  fullWidth
                  placeholder="Ref / ext ref / investor / currency / status..."
                  value={fundFlowFilters.search}
                  onChange={(e) => setFundFlowFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }} sx={{ display: 'flex', alignItems: 'center', minHeight: '56px' }}>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Switch
                      checked={fundFlowFilters.unallocatedOnly}
                      onChange={(e) => setFundFlowFilters(prev => ({ ...prev, unallocatedOnly: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body2" noWrap>Unallocated only</Typography>}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={fundFlowLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadFundFlowData}
                  sx={{ height: '56px' }}
                  disabled={fundFlowLoading}
                >
                  {fundFlowLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportFundFlow}
                  sx={{ height: '56px' }}
                  disabled={filteredFundFlowData.length === 0}
                >
                  Export Excel ({filteredFundFlowData.length})
                </Button>
              </Grid>
            </Grid>

            {fundFlowError && <Alert severity="error" sx={{ mb: 2 }}>{fundFlowError}</Alert>}

            {fundFlowData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Source Contract</TableCell>
                      <TableCell>Ext Ref</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Maturity</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Capital</TableCell>
                      <TableCell align="center">WHT %</TableCell>
                      <TableCell align="center">Gross-Up</TableCell>
                      <TableCell align="right">Rolled</TableCell>
                      <TableCell align="right">Paid Out</TableCell>
                      <TableCell align="right">Unallocated</TableCell>
                      <TableCell>Destinations</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredFundFlowData.map((row, idx) => {
                      const destinations = (row.rolled_to_contracts && row.rolled_to_contracts !== '[]')
                        ? (typeof row.rolled_to_contracts === 'string' ? JSON.parse(row.rolled_to_contracts) : row.rolled_to_contracts)
                        : [];
                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{row.source_contract_ref}</TableCell>
                          <TableCell>{row.source_user_ref || '-'}</TableCell>
                          <TableCell>
                            <Chip label={row.source_status} size="small" color={getStatusColor(row.source_status)} />
                          </TableCell>
                          <TableCell>{formatDate(row.source_maturity_date)}</TableCell>
                          <TableCell>{row.investor_name}</TableCell>
                          <TableCell>{row.currency_code}</TableCell>
                          <TableCell align="right">{formatAmt(row.investor_capital)}</TableCell>
                          <TableCell align="center">{row.wht_rate}%</TableCell>
                          <TableCell align="center">
                            <Chip label={row.wht_gross_up ? 'Yes' : 'No'} size="small"
                              color={row.wht_gross_up ? 'success' : 'default'} variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{formatAmt(row.total_rolled)}</TableCell>
                          <TableCell align="right">{formatAmt(row.total_paid_out)}</TableCell>
                          <TableCell align="right" sx={{
                            color: parseFloat(row.remaining_unallocated) > 0 ? 'warning.main' : undefined,
                            fontWeight: parseFloat(row.remaining_unallocated) > 0 ? 'bold' : undefined,
                          }}>
                            {formatAmt(row.remaining_unallocated)}
                          </TableCell>
                          <TableCell>
                            {destinations.map((d, i) => (
                              <Chip key={i} label={`${d.reference} (${formatAmt(d.rolled_amount)})`}
                                size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load rollover fund flow data
                </Typography>
              </Paper>
            )}

            {fundFlowData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredFundFlowData.length} record(s){filteredFundFlowData.length !== fundFlowData.length ? ` (filtered from ${fundFlowData.length})` : ''}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Rollover Suspense */}
      {activeReportFeature === 'contract_suspense' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  label="From Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={suspenseFilters.dateFrom}
                  onChange={(e) => setSuspenseFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  label="To Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={suspenseFilters.dateTo}
                  onChange={(e) => setSuspenseFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  label="Search"
                  fullWidth
                  placeholder="Ref / ext ref / investor / currency..."
                  value={suspenseFilters.search}
                  onChange={(e) => setSuspenseFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }} sx={{ display: 'flex', alignItems: 'center', minHeight: '56px' }}>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Switch
                      checked={suspenseFilters.unallocatedOnly}
                      onChange={(e) => setSuspenseFilters(prev => ({ ...prev, unallocatedOnly: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body2" noWrap>Unallocated only</Typography>}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={suspenseLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadSuspenseData}
                  sx={{ height: '56px' }}
                  disabled={suspenseLoading}
                >
                  {suspenseLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportSuspense}
                  sx={{ height: '56px' }}
                  disabled={filteredSuspenseData.length === 0}
                >
                  Export Excel ({filteredSuspenseData.length})
                </Button>
              </Grid>
            </Grid>

            {suspenseError && <Alert severity="error" sx={{ mb: 2 }}>{suspenseError}</Alert>}

            {suspenseData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Source Contract</TableCell>
                      <TableCell>Ext Ref</TableCell>
                      <TableCell>Finalisation Date</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Net Value</TableCell>
                      <TableCell align="right">Suspense</TableCell>
                      <TableCell align="right">Allocated</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell align="right">Paid Out (at finalisation)</TableCell>
                      <TableCell align="right">Paid Out (residual)</TableCell>
                      <TableCell>Destinations</TableCell>
                      {canPayoutReserve && <TableCell align="center">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSuspenseData.map((row, idx) => {
                      const destinations = (row.rolled_to_contracts && row.rolled_to_contracts !== '[]')
                        ? (typeof row.rolled_to_contracts === 'string' ? JSON.parse(row.rolled_to_contracts) : row.rolled_to_contracts)
                        : [];
                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{row.source_contract_ref}</TableCell>
                          <TableCell>{row.source_user_ref || '-'}</TableCell>
                          <TableCell>{formatDate(row.finalisation_date)}</TableCell>
                          <TableCell>{row.investor_name}</TableCell>
                          <TableCell>{row.currency_code}</TableCell>
                          <TableCell align="right">{formatAmt(row.net_value)}</TableCell>
                          <TableCell align="right">{formatAmt(row.suspense_amount)}</TableCell>
                          <TableCell align="right">{formatAmt(row.allocated_to_new_contracts)}</TableCell>
                          <TableCell align="right" sx={{
                            color: parseFloat(row.remaining_unallocated) > 0 ? 'warning.main' : undefined,
                            fontWeight: parseFloat(row.remaining_unallocated) > 0 ? 'bold' : undefined,
                          }}>
                            {formatAmt(row.remaining_unallocated)}
                          </TableCell>
                          <TableCell align="right">{formatAmt(row.payout_amount)}</TableCell>
                          <TableCell align="right">{formatAmt(row.reserve_paid_out)}</TableCell>
                          <TableCell>
                            {destinations.map((d, i) => (
                              <Chip key={i} label={`${d.reference} (${formatAmt(d.rolled_amount)})`}
                                size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </TableCell>
                          {canPayoutReserve && (
                            <TableCell align="center">
                              {parseFloat(row.remaining_unallocated) > 0.005 && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => {
                                    setReservePayoutContract({
                                      id: row.source_contract_id,
                                      reference: row.source_contract_ref,
                                      status: 'Finalised',
                                      currency_code: row.currency_code,
                                    });
                                    setReservePayoutDialogOpen(true);
                                  }}
                                >
                                  Pay Out
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load rollover suspense data
                </Typography>
              </Paper>
            )}

            {suspenseData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredSuspenseData.length} record(s){filteredSuspenseData.length !== suspenseData.length ? ` (filtered from ${suspenseData.length})` : ''}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Interest Accrual Journal */}
      {activeReportFeature === 'interest_accrual_journal' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={journalFilters.dateFrom}
                  onChange={(e) => setJournalFilters({ ...journalFilters, dateFrom: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={journalFilters.dateTo}
                  onChange={(e) => setJournalFilters({ ...journalFilters, dateTo: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1 }}>
                <Autocomplete
                  options={['', '__none__', ...journalSiteOptions]}
                  value={journalFilters.site || ''}
                  onChange={(_, newValue) => setJournalFilters({
                    ...journalFilters,
                    site: newValue || null,
                    overrideSiteForUnlinked: newValue === '__none__' ? journalFilters.overrideSiteForUnlinked : '',
                  })}
                  getOptionLabel={(option) => {
                    if (option === '' || option == null) return 'All Sites';
                    if (option === '__none__') return '(No Site)';
                    return option;
                  }}
                  isOptionEqualToValue={(option, val) => (option || '') === (val || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Site" placeholder="Search..." />
                  )}
                />
              </Grid>
              {/* SIL + Department (and the inline override when "(No Site)" is active)
                  share one cell that flex-splits, so all three shrink together
                  without expanding the overall row width. */}
              <Grid size={{ xs: 12, md: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {journalFilters.site === '__none__' && (
                    <TextField
                      sx={{ flex: 1, minWidth: 0 }}
                      label="Export site *"
                      inputProps={{ maxLength: 5, style: { textTransform: 'uppercase' } }}
                      value={journalFilters.overrideSiteForUnlinked}
                      onChange={(e) => setJournalFilters({
                        ...journalFilters,
                        overrideSiteForUnlinked: (e.target.value || '').toUpperCase(),
                      })}
                      error={
                        !!journalFilters.overrideSiteForUnlinked &&
                        journalFilters.overrideSiteForUnlinked.length !== 5
                      }
                    />
                  )}
                  <TextField
                    sx={{ flex: 1, minWidth: 0 }}
                    label="SIL"
                    value={journalFilters.sil}
                    onChange={(e) => setJournalFilters({ ...journalFilters, sil: e.target.value })}
                  />
                  <TextField
                    sx={{ flex: 1, minWidth: 0 }}
                    label="Department"
                    value={journalFilters.department}
                    onChange={(e) => setJournalFilters({ ...journalFilters, department: e.target.value })}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Ref / ext ref / investor / product / currency / narration / GL..."
                  value={journalFilters.search}
                  onChange={(e) => setJournalFilters({ ...journalFilters, search: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }} sx={{ display: 'flex', alignItems: 'center', minHeight: '56px' }}>
                <Tooltip title="Include previously exported entries in extract" arrow>
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Switch
                        checked={journalFilters.reExport}
                        onChange={(e) => setJournalFilters({ ...journalFilters, reExport: e.target.checked })}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2" noWrap>Incl. exported</Typography>}
                  />
                </Tooltip>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={journalLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadJournalData}
                  sx={{ height: '56px' }}
                  disabled={journalLoading || !journalFilters.dateFrom || !journalFilters.dateTo}
                >
                  {journalLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={journalExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleExportJournal}
                  sx={{ height: '56px' }}
                  disabled={
                    journalExporting ||
                    journalData.length === 0 ||
                    !journalFilters.dateFrom ||
                    !journalFilters.dateTo ||
                    (!journalFilters.reExport && journalData.filter(r => !r.journal_exported_at).length === 0) ||
                    (journalFilters.site === '__none__' && journalFilters.overrideSiteForUnlinked.length !== 5)
                  }
                >
                  {journalExporting ? 'Exporting...' : journalFilters.reExport
                    ? `Re-export All (${journalData.length})`
                    : `Extract to Excel (${journalData.filter(r => !r.journal_exported_at).length})`
                  }
                </Button>
              </Grid>
            </Grid>

            {journalError && (
              <Alert severity="error" sx={{ mb: 2 }}>{journalError}</Alert>
            )}

            {journalData.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {journalData.filter(r => !r.journal_exported_at).length} new entry(ies) ready for export.
                {journalData.filter(r => r.journal_exported_at).length > 0 &&
                  ` ${journalData.filter(r => r.journal_exported_at).length} previously exported.`}
              </Alert>
            )}

            {journalData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Contract Ref</TableCell>
                      <TableCell>External Ref</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell>Payment Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Interest</TableCell>
                      <TableCell>Expense GL</TableCell>
                      <TableCell>Control GL</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredJournalData.map((row, index) => {
                      const isReversal = row.narration === 'Interest Reversal';
                      const isReaccrual = row.narration === 'Interest Re-accrual';
                      const rowSx = {
                        ...(row.journal_exported_at ? { opacity: 0.6 } : {}),
                        ...(isReversal ? { backgroundColor: '#fff4e5' } : {}),
                        ...(isReaccrual ? { backgroundColor: '#e8f4fd' } : {}),
                      };
                      const typeChip = isReversal
                        ? <Chip label="Reversal" size="small" color="warning" />
                        : isReaccrual
                          ? <Chip label="Re-accrual" size="small" color="info" />
                          : <Chip label="Accrual" size="small" variant="outlined" />;
                      return (
                        <TableRow
                          key={row.schedule_id || index}
                          hover
                          sx={rowSx}
                          title={
                            (isReversal || isReaccrual) && row.reversal_source_schedule_id
                              ? `Paired with schedule entry #${row.reversal_source_schedule_id}`
                              : undefined
                          }
                        >
                          <TableCell>{row.contract_reference}</TableCell>
                          <TableCell>{row.user_reference || '-'}</TableCell>
                          <TableCell>{row.investor_name || '-'}</TableCell>
                          <TableCell>{renderPartyClassChip(row.party_class)}</TableCell>
                          <TableCell>{row.product_reference || row.product_type}</TableCell>
                          <TableCell>{row.currency_code}</TableCell>
                          <TableCell>{formatDate(row.payment_date)}</TableCell>
                          <TableCell>{typeChip}</TableCell>
                          <TableCell align="right">
                            {formatAmt(row.interest_amount)}
                          </TableCell>
                          <TableCell>{row.dr_expense_account || '-'}</TableCell>
                          <TableCell>{row.cr_control_account || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.journal_exported_at ? 'Exported' : 'New'}
                              size="small"
                              color={row.journal_exported_at ? 'default' : 'success'}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Set a date range and click "Generate Preview" to load interest accrual entries
                </Typography>
              </Paper>
            )}

            {journalData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredJournalData.length} entry(ies){filteredJournalData.length !== journalData.length ? ` (filtered from ${journalData.length})` : ''}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Issuer Ledger */}
      {activeReportFeature === 'issuer_ledger' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={ledgerFilters.dateFrom}
                  onChange={(e) => setLedgerFilters({ ...ledgerFilters, dateFrom: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={ledgerFilters.dateTo}
                  onChange={(e) => setLedgerFilters({ ...ledgerFilters, dateTo: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Issuer</InputLabel>
                  <Select
                    label="Issuer"
                    value={ledgerFilters.issuerId}
                    onChange={(e) => setLedgerFilters({ ...ledgerFilters, issuerId: e.target.value })}
                  >
                    <MenuItem value="">All Issuers</MenuItem>
                    {ledgerIssuerOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}{opt.site ? ` (${opt.site})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Investment Type</InputLabel>
                  <Select
                    label="Investment Type"
                    value={ledgerFilters.productId}
                    onChange={(e) => setLedgerFilters({ ...ledgerFilters, productId: e.target.value })}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {ledgerProductOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Ref / investor / type / GL..."
                  value={ledgerFilters.search}
                  onChange={(e) => setLedgerFilters({ ...ledgerFilters, search: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={ledgerLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadLedgerData}
                  sx={{ height: '56px' }}
                  disabled={ledgerLoading || !ledgerFilters.dateFrom || !ledgerFilters.dateTo}
                >
                  {ledgerLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={ledgerExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleExportLedger}
                  sx={{ height: '56px' }}
                  disabled={
                    ledgerExporting ||
                    ledgerData.rows.length === 0 ||
                    !ledgerFilters.dateFrom ||
                    !ledgerFilters.dateTo
                  }
                >
                  {ledgerExporting ? 'Exporting...' : 'Export Excel'}
                </Button>
              </Grid>
            </Grid>

            {ledgerError && (
              <Alert severity="error" sx={{ mb: 2 }}>{ledgerError}</Alert>
            )}

            {ledgerData.rows.length > 0 ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                /* slightly larger offset than the other reports — the Sub Ledger
                   header carries an extra description line */
                sx={{ maxHeight: 'calc(100vh - 390px)', minHeight: 400 }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Contract Ref</TableCell>
                      <TableCell>Ext Ref</TableCell>
                      <TableCell>Issuer</TableCell>
                      <TableCell>Investor</TableCell>
                      <TableCell>Party</TableCell>
                      <TableCell>Narration</TableCell>
                      <TableCell>Investment Type</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell align="center">Dr/Cr</TableCell>
                      <TableCell>GL</TableCell>
                      <TableCell align="center">Allocated</TableCell>
                      <TableCell>Allocation ID</TableCell>
                      <TableCell>Ccy</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Debit</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Credit</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Render the ledger grouped Issuer -> Investor -> Contract.
                      // Rows arrive pre-sorted from the SP. Opening/closing
                      // balances are per contract, labelled investor / ext ref.
                      // Amount/Outstanding show plain numbers (the Currency
                      // column already carries the ccy).
                      const out = [];
                      const fmtAmt = (v) => (v == null ? '-' : formatNum(parseFloat(v), dp));
                      // 15 columns: Date, ContractRef, ExtRef, Issuer, Investor,
                      // Narration, InvType, Class, Dr/Cr, GL, Ccy, Allocated,
                      // Debit, Credit, Balance.

                      // Per-(contract, investor) opening balances (from the SP
                      // helper). T3-078: the opening helper now returns one row per
                      // (contract, effective-investor), so key by the composite —
                      // each investor block under a multi-investor contract gets its
                      // own opening rather than one whole-contract figure.
                      const openingKeyOf = (contractId, investorId) => `${contractId}|${investorId}`;
                      const openingByContract = {};
                      (ledgerData.openings || []).forEach((o) => {
                        openingByContract[openingKeyOf(o.contract_id, o.investor_id)] = parseFloat(o.opening_balance) || 0;
                      });

                      let lastIssuer = null;
                      let lastInvestor = null;
                      let lastContract = null;
                      let closeCtx = null; // { ccy, val, investor, extRef } for the open contract
                      // Per-currency Dr/Cr accumulators for subtotals.
                      let investorTotals = {}; // ccy -> {dr, cr}
                      let issuerTotals = {};
                      const grandTotals = {};
                      let curInvestorLabel = '';
                      let curIssuerLabel = '';
                      const accAdd = (acc, ccy, drcr, amt) => {
                        const c = ccy || '';
                        if (!acc[c]) acc[c] = { dr: 0, cr: 0 };
                        if (drcr === 'Dr') acc[c].dr += amt; else acc[c].cr += amt;
                      };

                      const issuerLabelOf = (row) => (row.issuer_id == null
                        ? '(No Issuer)'
                        : `${row.issuer_name || 'Issuer'}${row.issuer_site ? ` (${row.issuer_site})` : ''}`);

                      // 17 cols: 1 Date 2 ContractRef 3 ExtRef 4 Issuer 5 Investor
                      // 6 Party 7 Narration 8 InvType 9 Class 10 Dr/Cr 11 GL 12 Allocated
                      // 13 AllocID 14 Ccy 15 Debit 16 Credit 17 Balance.
                      // (T3-065 added the Party column at 6.) A balance row: label in
                      // narration (7), ccy (14), value in Balance (17).
                      const balanceRow = (key, label, ccy, val, bg) => (
                        <TableRow key={key} sx={{ backgroundColor: bg }}>
                          <TableCell colSpan={6} />
                          <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                          <TableCell colSpan={6} />
                          <TableCell>{ccy}</TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtAmt(val)}</TableCell>
                        </TableRow>
                      );

                      // A subtotal row: label in narration (7), ccy (14), Dr in Debit (15), Cr in Credit (16).
                      const subtotalRows = (acc, label, bg) =>
                        Object.entries(acc).map(([ccy, t]) => (
                          <TableRow key={`sub-${label}-${ccy}`} sx={{ backgroundColor: bg }}>
                            <TableCell colSpan={6} />
                            <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                            <TableCell colSpan={6} />
                            <TableCell>{ccy}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtAmt(t.dr)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtAmt(t.cr)}</TableCell>
                            <TableCell />
                          </TableRow>
                        ));

                      const pushClosing = () => {
                        if (!closeCtx || closeCtx.val == null) return;
                        out.push(balanceRow(
                          `close-${closeCtx.contractId}-${closeCtx.investorKey}`,
                          `Closing balance — ${closeCtx.investor} / ${closeCtx.extRef || '-'}`,
                          closeCtx.ccy, closeCtx.val, '#f2f2f2'));
                      };
                      const flushInvestor = () => {
                        if (lastInvestor == null) return;
                        out.push(...subtotalRows(investorTotals, `Investor total (Dr / Cr movements) — ${curInvestorLabel}`, '#ddebf7'));
                      };
                      const flushIssuer = () => {
                        if (lastIssuer == null) return;
                        out.push(...subtotalRows(issuerTotals, `Issuer total (Dr / Cr movements) — ${curIssuerLabel}`, '#bdd7ee'));
                      };

                      filteredLedgerRows.forEach((row, index) => {
                        const issuerKey = row.issuer_id == null ? '__noiss__' : String(row.issuer_id);
                        // T3-078: group by the effective investor_id the report returns
                        // (was investor_name — a display string that could collide across
                        // two investors sharing a name on one contract). Label still uses
                        // the name.
                        const investorKey = row.investor_id == null ? '__noinv__' : String(row.investor_id);
                        const investorLabel = row.investor_name || '(No Investor)';
                        const contractKey = row.contract_id;

                        const issuerChanged = issuerKey !== lastIssuer;
                        const investorChanged = issuerChanged || investorKey !== lastInvestor;
                        const contractChanged = investorChanged || contractKey !== lastContract;

                        // Close the previous contract / investor / issuer in order.
                        if (contractChanged) pushClosing();
                        if (investorChanged) { flushInvestor(); investorTotals = {}; }
                        if (issuerChanged) { flushIssuer(); issuerTotals = {}; }

                        if (issuerChanged) {
                          lastIssuer = issuerKey;
                          lastInvestor = null;
                          curIssuerLabel = issuerLabelOf(row);
                          out.push(
                            <TableRow key={`iss-${issuerKey}-${index}`} sx={{ backgroundColor: '#bdd7ee' }}>
                              <TableCell colSpan={17} sx={{ fontWeight: 'bold' }}>{curIssuerLabel}</TableCell>
                            </TableRow>
                          );
                        }
                        if (investorChanged) {
                          lastInvestor = investorKey;
                          lastContract = null;
                          curInvestorLabel = investorLabel;
                          out.push(
                            <TableRow key={`inv-${issuerKey}-${investorKey}-${index}`} sx={{ backgroundColor: '#ddebf7' }}>
                              <TableCell colSpan={17} sx={{ pl: 3, fontWeight: 600 }}>{investorLabel}</TableCell>
                            </TableRow>
                          );
                        }
                        if (contractChanged) {
                          lastContract = contractKey;
                          const extRef = row.ext_reference || '';
                          out.push(
                            <TableRow key={`ctr-${contractKey}-${index}`} sx={{ backgroundColor: '#e2efda' }}>
                              <TableCell colSpan={17} sx={{ pl: 5, fontWeight: 600 }}>
                                {`${row.contract_reference}${extRef ? ` — ${extRef}` : ''}${row.product_type ? ` (${row.product_type})` : ''}`}
                              </TableCell>
                            </TableRow>
                          );
                          // Opening balance FIRST — before the movements. T3-078: look
                          // up the opening by (contract, investor) so each investor block
                          // under a multi-investor contract shows its own opening; the row
                          // key includes the investor so parallel blocks stay React-unique.
                          out.push(balanceRow(
                            `open-${contractKey}-${investorKey}`,
                            `Opening balance — ${investorLabel} / ${extRef || '-'}`,
                            row.currency_code || '', openingByContract[openingKeyOf(contractKey, row.investor_id)] || 0, '#f2f2f2'));
                          closeCtx = { contractId: contractKey, investorKey, investor: investorLabel, extRef, ccy: row.currency_code || '', val: null };
                        }

                        // Track last running balance for the contract closing balance.
                        if (row.running_balance != null && closeCtx) {
                          closeCtx.ccy = row.currency_code || '';
                          closeCtx.val = parseFloat(row.running_balance);
                        }

                        const amt = parseFloat(row.amount) || 0;
                        const isDr = row.dr_cr === 'Dr';
                        accAdd(investorTotals, row.currency_code, row.dr_cr, amt);
                        accAdd(issuerTotals, row.currency_code, row.dr_cr, amt);
                        accAdd(grandTotals, row.currency_code, row.dr_cr, amt);

                        out.push(
                          <TableRow key={`${row.schedule_id}-${row.class}-${index}`} hover>
                            <TableCell>{formatDate(row.payment_date)}</TableCell>
                            <TableCell>{row.contract_reference}</TableCell>
                            <TableCell>{row.ext_reference || '-'}</TableCell>
                            <TableCell>{row.issuer_id == null ? '(No Issuer)' : (row.issuer_name || '-')}</TableCell>
                            <TableCell>{row.investor_name || '-'}</TableCell>
                            <TableCell>{renderPartyClassChip(row.party_class)}</TableCell>
                            <TableCell>{row.narration || '-'}</TableCell>
                            <TableCell>{row.product_type || '-'}</TableCell>
                            <TableCell>{row.class}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.dr_cr}
                                size="small"
                                color={row.dr_cr === 'Dr' ? 'error' : 'success'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{row.gl_account || '-'}</TableCell>
                            <TableCell align="center">
                              {(() => {
                                // Allocated = settled via open-item matching (this leg has
                                // settle_status settled/partial, or any allocation id) OR the
                                // legacy matched-cash boolean. For an accrual leg the legacy
                                // boolean is false (cash points at the payment marker, not the
                                // accrual), so settle_status / allocation_ids drive it.
                                const isAllocated = row.settle_status === 'settled'
                                  || row.settle_status === 'partial'
                                  || !!row.allocation_ids
                                  || row.allocated;
                                const colour = isAllocated
                                  ? (row.settle_status === 'partial' ? 'warning' : 'success')
                                  : 'default';
                                const title = (row.settle_status && row.settle_status !== 'open' && row.open_amount != null)
                                  ? `${row.settle_status} — open ${fmtAmt(row.open_amount)} of ${fmtAmt(amt)}`
                                  : undefined;
                                return <Chip label={isAllocated ? 'Y' : 'N'} size="small" color={colour} title={title} />;
                              })()}
                            </TableCell>
                            <TableCell>{row.allocation_ids || '-'}</TableCell>
                            <TableCell>{row.currency_code || '-'}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{isDr ? fmtAmt(amt) : ''}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{isDr ? '' : fmtAmt(amt)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtAmt(row.running_balance)}</TableCell>
                          </TableRow>
                        );
                      });
                      // Flush trailing contract / investor / issuer.
                      pushClosing();
                      flushInvestor();
                      flushIssuer();
                      // Grand total.
                      out.push(...subtotalRows(grandTotals, 'GRAND TOTAL — all issuers (Dr / Cr movements, per currency)', '#fce4d6'));

                      // Control-account reconciliation: sum of each (contract, investor)
                      // closing balance, grouped by control account + currency. Ties this
                      // creditors subledger back to the GL control acct. T3-078: key the
                      // last-running-balance map by (contract_id, investor_id) — running
                      // balance is now per investor, so a contract-only key would keep only
                      // the LAST investor's closing balance and under-report the total for
                      // multi-investor contracts.
                      const controlRecon = {}; // acct -> { ccy -> sum }
                      const seenContract = {}; // "contractId|investorId" -> {acct, ccy, val}
                      filteredLedgerRows.forEach((row) => {
                        // last running balance per (contract, investor) = closing balance
                        if (row.running_balance != null) {
                          seenContract[openingKeyOf(row.contract_id, row.investor_id)] = {
                            acct: row.cr_account || '(no GL account)',
                            ccy: row.currency_code || '',
                            val: parseFloat(row.running_balance),
                          };
                        }
                      });
                      Object.values(seenContract).forEach(({ acct, ccy, val }) => {
                        if (!controlRecon[acct]) controlRecon[acct] = {};
                        controlRecon[acct][ccy] = (controlRecon[acct][ccy] || 0) + val;
                      });
                      const reconEntries = Object.entries(controlRecon);
                      if (reconEntries.length > 0) {
                        out.push(
                          <TableRow key="recon-header" sx={{ backgroundColor: '#fff2cc' }}>
                            <TableCell colSpan={17} sx={{ fontWeight: 'bold' }}>
                              Control Account Reconciliation — closing balance per control account &amp; currency (ties to GL)
                            </TableCell>
                          </TableRow>
                        );
                        // One section per control account: a bold account header,
                        // then one balance row per currency beneath it. 2100 and
                        // 002100 stay distinct (config difference surfaced, not merged).
                        reconEntries.forEach(([acct, byCcy]) => {
                          out.push(
                            <TableRow key={`recon-acct-${acct}`} sx={{ backgroundColor: '#fff8e1' }}>
                              <TableCell colSpan={6} />
                              <TableCell sx={{ fontWeight: 600 }}>Control account {acct}</TableCell>
                              <TableCell colSpan={3} />
                              <TableCell sx={{ fontWeight: 600 }}>{acct}</TableCell>
                              <TableCell colSpan={6} />
                            </TableRow>
                          );
                          Object.entries(byCcy).forEach(([ccy, val]) => {
                            // ccy col 14, value in Balance (col 17) — T3-065 shifted +1 (Party col)
                            out.push(
                              <TableRow key={`recon-${acct}-${ccy}`} sx={{ backgroundColor: '#fffbea' }}>
                                <TableCell colSpan={13} />
                                <TableCell>{ccy}</TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtAmt(val)}</TableCell>
                              </TableRow>
                            );
                          });
                        });
                      }
                      return out;
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Set a date range and click "Generate Preview" to load the sub ledger
                </Typography>
              </Paper>
            )}

            {ledgerData.rows.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredLedgerRows.length} ledger line(s){filteredLedgerRows.length !== ledgerData.rows.length ? ` (filtered from ${ledgerData.rows.length})` : ''}. Per-group Dr/Cr subtotals,
                issuer totals and a grand total are included in the Excel export (the export covers the full set, not the search-filtered preview).
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}
      {/* Audit Trail */}
      {activeReportFeature === 'audit_trail' && (
      <Box>
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={auditFilters.dateFrom}
                  onChange={(e) => setAuditFilters({ ...auditFilters, dateFrom: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 1.5 }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={auditFilters.dateTo}
                  onChange={(e) => setAuditFilters({ ...auditFilters, dateTo: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={auditFilters.action}
                    onChange={(e) => setAuditFilters({ ...auditFilters, action: e.target.value })}
                    label="Action Type"
                  >
                    <MenuItem value="all">All Actions</MenuItem>
                    <MenuItem value="INSERT">Create</MenuItem>
                    <MenuItem value="UPDATE">Update</MenuItem>
                    <MenuItem value="DELETE">Delete</MenuItem>
                    <MenuItem value="MERGE">Merge</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Table</InputLabel>
                  <Select
                    value={auditFilters.tableName}
                    onChange={(e) => setAuditFilters({ ...auditFilters, tableName: e.target.value })}
                    label="Table"
                  >
                    <MenuItem value="all">All Tables</MenuItem>
                    {auditTableOptions.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="User"
                  placeholder="changed by (email)..."
                  value={auditFilters.changedBy}
                  onChange={(e) => setAuditFilters({ ...auditFilters, changedBy: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 'grow' }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="User / table / record id / action..."
                  value={auditFilters.search}
                  onChange={(e) => setAuditFilters({ ...auditFilters, search: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  startIcon={auditLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={loadAuditData}
                  sx={{ height: '56px' }}
                  disabled={auditLoading}
                >
                  {auditLoading ? 'Loading...' : 'Generate Preview'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAudit}
                  sx={{ height: '56px' }}
                  disabled={filteredAuditData.length === 0}
                >
                  Export Excel ({filteredAuditData.length})
                </Button>
              </Grid>
            </Grid>

            {auditError && (
              <Alert severity="error" sx={{ mb: 2 }}>{auditError}</Alert>
            )}

            {auditData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Table</TableCell>
                      <TableCell>Record ID</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Amended By</TableCell>
                      <TableCell>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAuditData.map((audit, index) => (
                      <TableRow key={audit.id || index} hover>
                        <TableCell>{formatDate(audit.changed_at || audit.created_at)}</TableCell>
                        <TableCell>{audit.changed_by || audit.user_email || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={audit.action}
                            size="small"
                            color={
                              audit.action === 'INSERT' ? 'success' :
                              audit.action === 'DELETE' ? 'error' :
                              audit.action === 'MERGE' ? 'warning' : 'info'
                            }
                          />
                        </TableCell>
                        <TableCell>{audit.table_name}</TableCell>
                        <TableCell>{audit.record_id}</TableCell>
                        <TableCell>{audit.old_values?.created_by || audit.new_values?.created_by || '-'}</TableCell>
                        <TableCell>{audit.new_values?.updated_by || audit.old_values?.updated_by || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          {(() => {
                            const summary = summarizeAuditChange(audit);
                            if (!summary || summary === '-') return '-';
                            const truncated = summary.length > 120 ? summary.substring(0, 120) + '…' : summary;
                            return (
                              <Tooltip title={summary} arrow placement="top">
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                  {truncated}
                                </Typography>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  Click "Generate Preview" to load audit trail
                </Typography>
              </Paper>
            )}

            {auditData.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Showing {filteredAuditData.length} audit record(s)
                {filteredAuditData.length !== auditData.length ? ` (filtered from ${auditData.length})` : ''}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      )}

      {/* Reserve Payout dialog — launched from a Rollover Suspense report row */}
      <ReservePayoutDialog
        open={reservePayoutDialogOpen}
        onClose={() => setReservePayoutDialogOpen(false)}
        contract={reservePayoutContract}
        onSubmitSuccess={() => {
          setReservePayoutDialogOpen(false);
          loadSuspenseData();
          notify.success('Reserve payout submitted for approval');
        }}
      />
    </Box>
  );
}

export default ReportsPage;
